import type { EhrChangeDecision, ObservationHistory, PatientEhrChanges } from '../utils/ehrChanges';
import type { OnboardingScriptTemplate } from '../utils/onboardingScript';
import { normalizeClinicianBrief } from '../utils/clinicianBrief';
import type { ClinicianBrief } from '../utils/clinicianBrief';

// PHA-1225: one Program Confirmation Form texted to a patient, and the answer.
export interface ConsentRequest {
  id: string;
  form_text: string;
  consent_question: string;
  /** The language the form was shown in (en-US when the clinic had no wording in another). */
  language?: string | null;
  sms_body: string | null;
  send_status: 'sending' | 'sent' | 'failed';
  send_error: string | null;
  sent_by_name: string | null;
  sent_at: string | null;
  expires_at: string | null;
  expired: boolean;
  response: 'yes' | 'no' | null;
  responded_at: string | null;
  ehr_push_status: 'pending' | 'accepted' | 'filed' | 'failed' | null;
  ehr_pushed_at: string | null;
  ehr_push_error: string | null;
}

export interface ConsentRequestList {
  requests: ConsentRequest[];
  ehr_push_enabled: boolean;
}

// Environment Variables Logging
const authDomain = process.env.REACT_APP_AUTH0_DOMAIN;
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
const redirectUri = process.env.REACT_APP_AUTH0_REDIRECT_URI;
const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
const vapiEnabled = process.env.REACT_APP_ENABLE_VAPI_CALLS;

console.log('🔧 Environment Variables:');
console.log('Auth0 Domain:', authDomain);
console.log('Client ID:', clientId);
console.log('Redirect URI:', redirectUri);
console.log('API Base URL:', apiBaseUrl);
console.log('VAPI Enabled:', vapiEnabled);

// API Service Configuration
const API_CONFIG = {
  // Use production server by default, fallback to localhost for development
  BASE_URL: apiBaseUrl || 'https://phanova.coveredornot.com',
  ENDPOINTS: {
    // Authentication
    LOGIN: '/login',
    LOGOUT: '/logout',
    CALLBACK: '/callback',
    USER_MATCH: '/session/user/spa_match',
    
    // Patient Management
    PATIENT_LIST: '/session/patient/list',
    PATIENT_CREATE: '/session/patient/create',
    PATIENT_UPDATE: '/session/patient/update',
    PATIENT_DETAIL: '/session/patient/details',
    
    // Document Management
    DOCUMENT_UPLOAD: '/session/patient/document/upload',
    DOCUMENT_LIST: '/session/patient/document/list',
    
    // Coverage Determination
    PROCESS_PATIENT: '/process_patient_in_guideline',
    COVERAGE_LIST: '/session/coverage_det/list',
  }
};

// API Helper Functions
export const getApiUrl = (endpoint: string) => {
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint;
  }
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// PHA-810: transient backend blips (auth couldn't reach the DB) now surface as a
// retryable 503 instead of a 401. Retry those a couple of times before bubbling an
// error, so a brief hiccup never reaches the user as a failure or a forced logout.
const API_MAX_ATTEMPTS = 3;
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// PHA-940: POSTs that only read. A 502 or a dropped connection on one of these is retried
// like a GET, because repeating a read can't double-apply anything. The 502s seen here come
// from nginx, not the app (the body is nginx's HTML page, hence "Server Error (502)"): the
// BFF was briefly unreachable, e.g. mid-restart, and a manual retry a moment later worked.
// Keep in step with TRANSIENT_RETRYABLE_POST_ENDPOINTS in bff-server/server.js, which covers
// the BFF -> Python hop. Never add a write here.
export const READ_ONLY_POST_ENDPOINTS = new Set<string>([
  '/session/patient/list_with_details',
  '/session/patient/list',
  '/session/patient/details',
  '/session/patient/vitals',
  '/session/patient/document_names',
  '/session/patient/items/candidates',
  '/session/patient/items/review_draft',
  '/session/patient/care_plan_definition/candidates',
  '/session/patient/care_plan_definition/list',
  '/session/patient/care_plan_definition/match',
  '/session/patient/care_plan_definition/draft_meta',
  '/session/patient/care_plan_definition/draft_apcm_tier',
  '/session/patient/care_plan_review/list',
  '/session/patient/ehr_changes',
  '/session/organization/voice_enabled',
  '/session/organization/voice_of_patient_enabled',
  '/session/organization/no_target_dates_enabled',
  '/session/organization/has_ehr_integration',
  '/session/organization/onboarding_script_template',
  '/session/patient/consent/list',
  '/session/patient/consent/pdf',
]);
const GATEWAY_RETRY_DELAY_MS = 750;

export const apiRequest = async (endpoint: string, options: RequestInit = {}, _attempt: number = 1): Promise<Response> => {
  const url = getApiUrl(endpoint);
  const method = (options.method || 'GET').toUpperCase();
  const isIdempotent = method === 'GET' || method === 'HEAD';
  const isRetryableRead = isIdempotent || READ_ONLY_POST_ENDPOINTS.has(endpoint);

  const defaultOptions: RequestInit = {
    mode: 'cors',
    credentials: 'include', // Always include cookies for session-based authentication
    redirect: 'manual', // Don't follow redirects automatically - we'll handle them manually
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers,
    },
  };

  try {
    console.log(`🔍 API Request: ${options.method || 'GET'} ${url}`);
    console.log(`🍪 Using session cookies for authentication`);
    console.log(`🌐 Backend URL: ${API_CONFIG.BASE_URL}`);
    console.log(`📋 Request headers:`, defaultOptions.headers);
    
    const fetchStart = performance.now();
    const response = await fetch(url, { ...defaultOptions, ...options });
    const fetchDuration = performance.now() - fetchStart;

    console.log(`📡 API Response: ${response.status} ${response.statusText} (${Math.round(fetchDuration)}ms)`);
    console.log(`📡 Response URL: ${response.url}`);
    console.log(`🍪 Response cookies:`, response.headers.get('Set-Cookie'));
    
    // Handle authentication failures - DON'T redirect automatically
    if (response.status === 401) {
      console.log(`🔒 Authentication failed - NOT auto-redirecting, require manual login`);
      throw new Error('Authentication required - please login manually');
    }
    
    // Handle redirects manually to avoid CORS issues with Auth0
    if (response.type === 'opaqueredirect' || response.status === 0 || response.status === 302 || response.status === 307) {
      console.log(`🔀 Opaque/Manual redirect detected, response type: ${response.type}, status: ${response.status}`);
      
      // For opaque redirects (status 0), we can't read the Location header due to CORS
      // This means the backend is redirecting to Auth0 - DON'T auto-redirect
      if (response.type === 'opaqueredirect' || response.status === 0) {
        console.log(`🔒 Opaque redirect detected - authentication needed but NOT auto-redirecting`);
        throw new Error('Authentication required - please login manually');
      }
      
      // For other redirects, try to get the Location header
      const location = response.headers.get('Location');
      console.log(`🔀 Redirect location: ${location}`);
      
      // If it's a redirect to login or auth, DON'T auto-redirect
      if (location && (location.includes('login') || location.includes('auth0.com') || location.includes('authorize'))) {
        console.log(`🔒 Authentication redirect detected - NOT auto-redirecting`);
        throw new Error('Authentication required - please login manually');
      }
      
      // For other redirects, follow them manually
      if (location) {
        console.log(`🔄 Following redirect to: ${location}`);
        const redirectUrl = location.startsWith('http') ? location : `${API_CONFIG.BASE_URL}${location}`;
        return apiRequest(redirectUrl, options);
      }
      
      // If no location header but we got a redirect, assume it's auth-related
      console.log(`🔒 Redirect without location header - assuming authentication needed`);
      throw new Error('Authentication required - please login manually');
    }
    
    // PHA-810: 503 = transient backend unavailability (e.g. auth couldn't reach the
    // DB). This is raised before any endpoint side effect, so it is always safe to
    // retry — including for POSTs. Honor Retry-After when present.
    if (response.status === 503 && _attempt < API_MAX_ATTEMPTS) {
      const retryAfterRaw = parseInt(response.headers.get('Retry-After') || '', 10);
      const delayMs = Number.isFinite(retryAfterRaw) ? retryAfterRaw * 1000 : 300 * _attempt;
      console.log(`⏳ 503 from ${endpoint} - retrying attempt ${_attempt + 1}/${API_MAX_ATTEMPTS} in ${delayMs}ms`);
      await sleep(delayMs);
      return apiRequest(endpoint, options, _attempt + 1);
    }

    // PHA-940: 502 = nginx could not reach the BFF. Safe to repeat for a read.
    if (response.status === 502 && isRetryableRead && _attempt < API_MAX_ATTEMPTS) {
      const delayMs = GATEWAY_RETRY_DELAY_MS * _attempt;
      console.log(`⏳ 502 from ${endpoint} - retrying attempt ${_attempt + 1}/${API_MAX_ATTEMPTS} in ${delayMs}ms`);
      await sleep(delayMs);
      return apiRequest(endpoint, options, _attempt + 1);
    }

    // Check for other error statuses
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error(`❌ API Error Response (Status ${response.status}):`, errorText);
      
      // Try to parse error response as JSON
      let errorData: any = { error: response.statusText, details: errorText };
      try {
        const parsedError = JSON.parse(errorText);
        console.log('✅ Parsed error as JSON:', parsedError);
        errorData = {
          error: parsedError.error || response.statusText,
          details: parsedError.details || errorText,
          status: response.status,
          endpoint: endpoint,
        };
      } catch (e) {
        console.log('⚠️ Error response is not JSON, using raw text');
        // If not JSON, use raw text
        errorData = {
          error: `Server Error (${response.status})`,
          details: errorText,
          status: response.status,
          endpoint: endpoint,
        };
      }
      
      console.error('❌ Structured error data:', errorData);
      
      // Create a structured error with the data
      const error = new Error(errorData.error) as any;
      error.errorData = errorData;
      
      console.error('❌ Throwing error with errorData:', error.errorData);
      throw error;
    }
    
    return response;
    
  } catch (error: any) {
    console.error(`❌ API Request Failed:`, error);
    
    // Handle CORS errors specifically
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      // PHA-810: a transient network blip on an idempotent request is worth a retry
      // before surfacing a hard error. Only read-only POSTs are retried (PHA-940); others may
      // have side effects.
      if (isRetryableRead && _attempt < API_MAX_ATTEMPTS) {
        console.log(`⏳ Network error on ${endpoint} - retrying attempt ${_attempt + 1}/${API_MAX_ATTEMPTS}`);
        await sleep(300 * _attempt);
        return apiRequest(endpoint, options, _attempt + 1);
      }
      console.error('❌ CORS/Network Error:', error.message);
      console.error('🌐 Trying to connect to:', url);
      throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
    }
    
    // Re-throw other errors
    throw error;
  }
};

// Specific API calls
export const authApi = {
  checkUserSession: async () => {
    const response = await apiRequest(API_CONFIG.ENDPOINTS.USER_MATCH, { 
      method: 'POST',
      body: JSON.stringify({}),
      credentials: 'include'
    });
    return response.json();
  },
  // Use environment variable for backend URL
  logout: () => window.location.href = `${API_CONFIG.BASE_URL}/logout`,
  login: () => window.location.href = `${API_CONFIG.BASE_URL}/login`,
  
  // Session management
  extendSession: async () => {
    const response = await apiRequest('/session/extend', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return response.json();
  },
  
  getTimeoutInfo: async () => {
    const response = await apiRequest('/session/timeout_info', {
      method: 'GET',
    });
    return response.json();
  },
};

export const patientApi = {
  // PHA-466 stage 1: match patient chart to curated care plan templates.
  // PHA-1157: `useStagedReview` matches a regeneration's staged Review-step problem list (the
  // set its generation reads), so a diagnosis added in that review is offered in the picker.
  matchCarePlanDiseases: async (patientId: string, useStagedReview = false) => {
    const response = await apiRequest('/session/patient/care_plan_definition/match', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        ...(useStagedReview ? { use_staged_review: true } : {}),
      })
    });
    return response.json();
  },

  list: async () => {
    const response = await apiRequest('/session/patient/list', { 
      method: 'POST',
      body: JSON.stringify({})
    });
    return response.json();
  },
  
  /**
   * PHA-1083: every paging/search/sort/filter field is optional. Omitting them
   * all returns the whole list, which is what this did before pagination — the
   * CSV export relies on that, and so does the Chief of Staff agent.
   */
  listWithDetails: async (
    signal?: AbortSignal,
    organizationId?: string,
    options?: {
      page?: number;
      pageSize?: number;
      search?: string;
      sortField?: string;
      sortDirection?: 'asc' | 'desc';
      filters?: Record<string, any>;
      /**
       * Drop the raw survey answers and most-recent-surveys blobs, and return
       * the latest blood pressure as three scalar columns instead. The grid
       * used those blobs for exactly one thing — getLatestBloodPressure — and
       * they are ~45% of the payload. Off by default, so callers that do read
       * the raw answers are unaffected.
       */
      lean?: boolean;
    },
  ) => {
    const body: Record<string, any> = {};
    if (organizationId) body.organization_id = organizationId;
    if (options?.lean) body.lean = true;
    if (options?.pageSize != null) body.page_size = options.pageSize;
    if (options?.page != null) body.page = options.page;
    if (options?.search) body.search = options.search;
    if (options?.sortField) {
      body.sort = { field: options.sortField, direction: options.sortDirection || 'asc' };
    }
    if (options?.filters && Object.keys(options.filters).length > 0) {
      body.filters = options.filters;
    }
    const response = await apiRequest('/session/patient/list_with_details', {
      method: 'POST',
      body: JSON.stringify(body),
      signal,
    });
    return response.json();
  },
  
  create: async (patientData: any) => {
    const response = await apiRequest('/session/patient/create', {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
    return response.json();
  },

  update: async (patientData: any) => {
    const response = await apiRequest('/session/patient/update', {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
    return response.json();
  },

  updatePcm: async (payload: Record<string, unknown>) => {
    const response = await apiRequest('/session/patient/pcm/update', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.json();
  },

  extractPcmIntake: async (patientId: string, pcm_elicitation_narrative?: string) => {
    const response = await apiRequest('/session/patient/pcm/extract_intake', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        ...(pcm_elicitation_narrative != null ? { pcm_elicitation_narrative } : {}),
      }),
    });
    return response.json();
  },

  // PHA-1162: deferSurveys during first-time onboarding — the goals are saved but surveys
  // are left to completeOnboarding (PHA-895), since the patient is not enrolled yet.
  approvePcmIntake: async (patientId: string, approved?: unknown, deferSurveys = false) => {
    const response = await apiRequest('/session/patient/pcm/approve_intake', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        ...(approved != null ? { approved } : {}),
        ...(deferSurveys ? { defer_surveys: true } : {}),
      }),
    });
    return response.json();
  },

  // PHA-851: remove a single Voice-of-the-Patient goal by pco_ref. Additive —
  // peer goals and their answers are preserved (backend cascades the goal's own
  // GAS def, measurements and barriers).
  removePcmGoal: async (patientId: string, pcoRef: string) => {
    const response = await apiRequest('/session/patient/pcm/remove_goal', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        pco_ref: pcoRef,
      }),
    });
    return response.json();
  },

  // PHA-795 (flow redesign): generate the GAS scale + tracking question + barriers for a
  // single chosen goal (the "Extract Goals and barriers" button). Read-only — returns
  // { detail: { goals: [oneGoal w/ gas+tracking_question], barriers: [...] } }; the panel
  // reviews it and then persists via approvePcmIntake.
  generatePcmGoalDetails: async (patientId: string, goalText: string) => {
    const response = await apiRequest('/session/patient/pcm/generate_goal_details', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        goal_text: goalText,
      }),
    });
    return response.json();
  },

  get: async (id: string, organizationId?: string) => {
    const response = await apiRequest('/session/patient/details', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: id,
        ...(organizationId ? { organization_id: organizationId } : {}),
      }),
    });
    return response.json();
  },

  // Get item candidates for review
  getCandidates: async (patientId: string) => {
    const response = await apiRequest('/session/patient/items/candidates', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId
      }),
    });
    return response.json();
  },

  // Approve extracted items (problems or medications)
  approveItems: async (patientId: string, documentType: 'problem_list' | 'medication_list', reviewedItems: any[]) => {
    const response = await apiRequest('/session/patient/items/approve', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        document_type: documentType,
        reviewed_items: reviewedItems
      }),
    });
    return response.json();
  },

  // PHA-1158: advisory ICD-10 suggestions for problems that have no code (nothing is written).
  suggestProblemIcd10: async (patientId: string, problems: string[]) => {
    const response = await apiRequest('/session/patient/items/suggest_icd10', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId, problems }),
    });
    return response.json();
  },

  // PHA-1067: stage a care-plan REGENERATION's problem/medication review instead of approving
  // it. Same payload as approveItems, but the decisions are parked server-side until the care
  // plan is approved — approveItems writes straight through to the patient's LIVE lists (the
  // problem_list / medication_list views read the candidate rows it rewrites), which meant an
  // abandoned or cancelled regeneration left its rejections in force with no way back.
  //
  // `carePlanUploadedTs` is the draft care-plan batch these decisions belong to, when there
  // already is one on screen (a RESUMED regeneration). Only a staged review linked to the batch
  // being approved is promoted, and staging otherwise clears that link — so without this,
  // re-entering Review on a saved draft and pressing Continue would leave the decisions
  // unpromotable and they would vanish on approval. Omitted at the start of a regeneration,
  // where nothing has been generated yet and the link must indeed be cleared.
  stageItems: async (
    patientId: string,
    documentType: 'problem_list' | 'medication_list',
    reviewedItems: any[],
    carePlanUploadedTs?: string[],
  ) => {
    const response = await apiRequest('/session/patient/items/stage', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        document_type: documentType,
        reviewed_items: reviewedItems,
        ...(carePlanUploadedTs && carePlanUploadedTs.length > 0
          ? { care_plan_uploaded_ts: carePlanUploadedTs }
          : {}),
      }),
    });
    return response.json();
  },

  // PHA-1067: the staged regeneration review, in the same shape as getCandidates
  // ({ problem_list: { extracted_data, reviewed }, ... }); {} when nothing is staged.
  getItemReviewDraft: async (patientId: string) => {
    const response = await apiRequest('/session/patient/items/review_draft', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
    return response.json();
  },

  // Generate care plan
  getPatientSurveyTemplates: async (patientId: string) => {
    const response = await apiRequest('/session/patient/survey-templates', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
    return response.json();
  },

  // PHA-1067: `useStagedReview` says this generation belongs to a regeneration wizard session,
  // whose problem/medication review is staged rather than committed — without it the draft
  // would be generated from the patient's previous lists and silently ignore the review. Only
  // a generation that sets it can promote that staged review at approval time, so an abandoned
  // staging never grounds an unrelated plan.
  //
  // PHA-1157: a regeneration (`useStagedReview`) starts from the previous approved plan's edits.
  // `resetToGuidelines` opts out of that for one generation - "Reset to standard guidelines".
  generateCarePlan: async (
    patientId: string,
    selectedDiseases?: string[],
    useStagedReview = false,
    resetToGuidelines = false,
  ) => {
    const response = await apiRequest('/session/patient/care_plan_definition/generate', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        ...(selectedDiseases?.length ? { selected_diseases: selectedDiseases } : {}),
        use_staged_review: useStagedReview,
        ...(resetToGuidelines ? { reset_to_guidelines: true } : {}),
      }),
    });
    return response.json();
  },

  // PHA-863: re-parse the patient's originally-uploaded documents to re-derive their
  // problem/medication lists ("Regenerate from original documents" — Mode B).
  // Returns { patient_id, documents: [{ document_type, extracted_items, ... }] }.
  reparseOriginalDocuments: async (patientId: string) => {
    const response = await apiRequest('/session/patient/care_plan/reparse_original_documents', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
    return response.json();
  },

  // Get draft care plan candidates (generated but not yet approved).
  // ehrOnly=true restricts to drafts from the daily EHR refresh (trigger_source='ehr_cron'),
  // used by the "EHR refresh pending review" banner so it doesn't fire on manual/onboarding drafts.
  // PHA-1050: manualOnly=true is the mirror of ehrOnly — drafts a CLINICIAN started
  // (trigger_source not 'ehr_cron'). The chart uses it to tell an in-flight regeneration
  // apart from the nightly EHR review queue. Both false = every unapproved draft.
  getCarePlanCandidates: async (patientId: string, ehrOnly = false, manualOnly = false) => {
    const response = await apiRequest('/session/patient/care_plan_definition/candidates', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        ehr_only: ehrOnly,
        manual_only: manualOnly
      }),
    });
    return response.json();
  },

  // Get care plans for a patient
  getCarePlans: async (patientId: string) => {
    const response = await apiRequest('/session/patient/care_plan_definition/list', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId
      }),
    });
    return response.json();
  },

  // Delete patient
  delete: async (patientId: string) => {
    const response = await apiRequest('/session/patient/delete', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId
      }),
    });
    return response.json();
  },

  // Approve care plan definitions.
  // PHA-895: deferSurveys=true (onboarding) skips survey generation at approval so a patient is
  // never surveyed before they are fully registered — surveys are generated by completeOnboarding
  // instead. Existing-patient re-approval passes false and generates surveys inline as before.
  // PHA-983: whether "Push Care Plan to EMR" should appear for this patient, plus the last push.
  getCarePlanEhrPushStatus: async (patientId: string) => {
    const response = await apiRequest(
      `/session/patient/care_plan/ehr_push_status?patient_id=${encodeURIComponent(patientId)}`
    );
    return response.json();
  },

  // PHA-983: file the approved care plan PDF into the EHR.
  //
  // Sent as multipart because what the browser holds is a Blob. `headers: {}` is deliberate
  // and load-bearing: apiRequest defaults to 'Content-Type: application/json', and leaving
  // that in place stops fetch from generating the multipart boundary, so the upload arrives
  // unparseable. Passing an empty object replaces the default headers entirely and lets
  // fetch set the correct Content-Type for the FormData.
  //
  // `automatic` marks a PHA-1204 upload made on approval rather than by the button, so a
  // failure can tell the clinician the automatic attempt failed and to push it manually.
  pushCarePlanToEhr: async (
    patientId: string,
    pdf: Blob,
    filename: string,
    force = false,
    automatic = false,
  ) => {
    const form = new FormData();
    form.append('patient_id', patientId);
    form.append('force', force ? 'true' : 'false');
    form.append('automatic', automatic ? 'true' : 'false');
    form.append('document', pdf, filename);

    let response: Response;
    try {
      response = await apiRequest('/session/patient/care_plan/push_to_ehr', {
        method: 'POST',
        headers: {},
        body: form,
      });
    } catch (err: any) {
      // PHA-983 (Tom's "re-sending fails"): apiRequest THROWS on any non-ok status, so it
      // never hands back a non-ok Response — a `if (!response.ok)` check here would be dead
      // code, which is exactly the bug this replaces. The 409 has to be recovered from the
      // thrown error instead, and the status lives on `errorData`, not on the error itself.
      //
      // 409 means this exact plan version is already filed. It is a confirmable state, not a
      // failure: the caller turns it into a "send another copy?" prompt. Losing the flag is
      // what made every re-send surface as a red error toast the clinician could not act on.
      if (err?.errorData?.status === 409) {
        err.alreadySent = true;
        err.status = 409;
      }
      throw err;
    }
    return response.json();
  },

  approveCarePlanDefinitions: async (
    patientId: string,
    reviewedItems: any[],
    questionOverrides?: Record<string, string>,
    generalQuestions?: any[],
    deferSurveys = false,
  ) => {
    const response = await apiRequest('/session/patient/care_plan_definition/approve', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        reviewed_items: reviewedItems,
        question_overrides: questionOverrides || {},
        general_questions: generalQuestions || [],
        defer_surveys: deferSurveys,
      }),
    });
    return response.json();
  },

  // PHA-895: finalize onboarding — records consent AND generates the (deferred) surveys, then
  // returns them so the caller can mint patient-facing survey links. Forwards the same
  // question_overrides / general_questions used at approval so generation is identical.
  completeOnboarding: async (
    patientId: string,
    consentStatus: 'collected' | 'denied',
    questionOverrides?: Record<string, string>,
    generalQuestions?: any[],
  ) => {
    const response = await apiRequest('/session/patient/onboard/complete', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        consent_status: consentStatus,
        question_overrides: questionOverrides || {},
        general_questions: generalQuestions || [],
      }),
    });
    return response.json();
  },

  // Save care plan edits to draft tables without approving (PHA-737).
  // PHA-895: also persists the on-screen expiration date + consent selection so a resumed draft
  // restores them (they are draft-scoped meta, NOT the live patients.consent_status).
  saveCarePlanDraft: async (
    patientId: string,
    reviewedItems: any[],
    questionOverrides?: Record<string, string>,
    generalQuestions?: any[],
    expirationDate?: string | null,
    consentStatus?: 'yes' | 'no' | null,
  ) => {
    const response = await apiRequest('/session/patient/care_plan_definition/save_draft', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        reviewed_items: reviewedItems,
        question_overrides: questionOverrides || {},
        general_questions: generalQuestions || [],
        expiration_date: expirationDate ?? null,
        consent_status: consentStatus ?? null,
      }),
    });
    return response.json();
  },

  // PHA-1049: "Cancel Care Plan Regeneration" — discard the in-flight regeneration draft
  // and leave the patient's approved care plan exactly as it was. Server-side this only
  // removes UNAPPROVED, manually-triggered draft batches; ehr_cron review drafts survive.
  // PHA-897: record that the care plan was reviewed WITH the patient. This is the refresh
  // flow's replacement for the consent step — an acknowledgment per care-plan update, NOT
  // programme consent (which is one-time and drives the billing start date, PHA-953).
  recordCarePlanAcknowledgment: async (
    patientId: string,
    carePlanUploadedTs?: string | null,
    acknowledgedByName?: string | null,
  ) => {
    const response = await apiRequest('/session/patient/care_plan/acknowledgment', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        care_plan_uploaded_ts: carePlanUploadedTs ?? null,
        acknowledged_by_name: acknowledgedByName ?? null,
      }),
    });
    return response.json();
  },

  // PHA-726: acknowledgment history, newest first.
  listCarePlanAcknowledgments: async (patientId: string) => {
    const response = await apiRequest('/session/patient/care_plan/acknowledgment/list', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
    return response.json();
  },

  // PHA-1050: pass the uploaded_ts of the batch THIS session generated. Server-side the
  // delete is then scoped to it, so a cancel can never reach another in-flight draft — which
  // matters now that a draft-only patient can start a regeneration at all (PHA-945).
  discardCarePlanDraft: async (patientId: string, uploadedTs?: string[]) => {
    const response = await apiRequest('/session/patient/care_plan_definition/discard_draft', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        ...(uploadedTs && uploadedTs.length > 0 ? { uploaded_ts: uploadedTs } : {}),
      }),
    });
    return response.json();
  },

  // PHA-737: prospective APCM tier for the current draft care plan. patients.apcm_tier
  // is NULL until approval, so the Print Draft PDF can't read it directly — this computes
  // the tier the patient WOULD get if the draft were approved now, so the draft PDF's
  // program label (APCM vs CCM) matches the final post-approval PDF. Returns 1, 2, or null.
  getDraftApcmTier: async (patientId: string): Promise<number | null> => {
    const response = await apiRequest('/session/patient/care_plan_definition/draft_apcm_tier', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId
      }),
    });
    const data = await response.json();
    return data?.apcm_tier ?? null;
  },

  // PHA-1195: the printed care plan's texts in the patient's language (resolved server-side
  // from the patient row). Texts that could not be translated are absent from `translations`.
  // `privateTexts` (notes, the patient's own goals) are translated but never cached server-side.
  translateCarePlanPdfTexts: async (
    patientId: string,
    texts: string[],
    privateTexts: string[] = []
  ): Promise<{ language: string; translations: Record<string, string> }> => {
    const response = await apiRequest('/session/patient/care_plan_pdf/translate', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId, texts, private_texts: privateTexts }),
    });
    const data = await response.json();
    return { language: data?.language ?? 'en-US', translations: data?.translations ?? {} };
  },

  // PHA-895: draft-scoped expiration date (MM/DD/YYYY) + consent selection ('yes'/'no'),
  // restored when a saved onboarding draft is resumed. Returns nulls when no draft meta exists.
  getCarePlanDraftMeta: async (
    patientId: string,
  ): Promise<{ expiration_date: string | null; consent_status: 'yes' | 'no' | null }> => {
    const response = await apiRequest('/session/patient/care_plan_definition/draft_meta', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
    const data = await response.json();
    return {
      expiration_date: data?.expiration_date ?? null,
      consent_status: (data?.consent_status ?? null) as 'yes' | 'no' | null,
    };
  },

  // Update care plan expiration date
  updateCarePlanExpiration: async (patientId: string, disease: string, uploadedTs: string, expirationDate: string) => {
    const response = await apiRequest('/session/patient/care_plan_definition/update', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        disease,
        uploaded_ts: uploadedTs,
        expiration_date: expirationDate,
      }),
    });
    return response.json();
  },

  // PHA-870: list a patient's care-plan review records (newest first).
  getCarePlanReviews: async (patientId: string) => {
    const response = await apiRequest('/session/patient/care_plan_review/list', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
      }),
    });
    return response.json();
  },

  // PHA-870: save a care-team review of a patient's care plan.
  // reviewDate must be an ISO date (YYYY-MM-DD), not in the future.
  saveCarePlanReview: async (
    patientId: string,
    reviewDate: string,
    patientComments: string,
    actionTaken: string,
  ) => {
    const response = await apiRequest('/session/patient/care_plan_review/save', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        review_date: reviewDate,
        patient_comments: patientComments,
        action_taken: actionTaken,
      }),
    });
    return response.json();
  },

  // PHA-870: edit a review. Non-destructive — appends a new version server-side; prior
  // versions are retained. `id` is the review's logical id from the list.
  updateCarePlanReview: async (
    patientId: string,
    id: number,
    reviewDate: string,
    patientComments: string,
    actionTaken: string,
  ) => {
    const response = await apiRequest('/session/patient/care_plan_review/update', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        id,
        review_date: reviewDate,
        patient_comments: patientComments,
        action_taken: actionTaken,
      }),
    });
    return response.json();
  },

  // PHA-870: delete a review. Non-destructive — soft delete server-side (the record and
  // its history are retained). `id` is the review's logical id from the list.
  deleteCarePlanReview: async (patientId: string, id: number) => {
    const response = await apiRequest('/session/patient/care_plan_review/delete', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        id,
      }),
    });
    return response.json();
  },

  // Create survey link
  // PHA-1261: staffEntry=true only when staff open the link themselves ("Show survey link") —
  // a response submitted from it is then recorded as entered by that staff user. Never set it
  // for a link that may reach the patient.
  createSurveyLink: async (surveyId: string, patientId: string, staffEntry = false) => {
    const response = await apiRequest(`/survey/${surveyId}/create_link`, {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        ...(staffEntry ? { staff_entry: true } : {})
      }),
    });
    return response.json();
  },

  // Save patient consent and mark onboarding complete
  savePatientConsent: async (patientId: string, consent: boolean) => {
    const response = await apiRequest('/session/patient/onboard/complete', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId, consent }),
    });
    return response.json();
  },

  // Send survey link via SMS
  sendSurveyLink: async (surveyId: string, patientId: string, phoneNumber: string) => {
    const response = await apiRequest(`/survey/${surveyId}/send_link`, {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        phone_number: phoneNumber
      }),
    });
    return response.json();
  },

  // Create custom survey for a patient
  createCustomSurvey: async (patientId: string, surveyData: {
    name: string;
    questions: { prompt: string; data_type: string }[];
  }) => {
    const response = await apiRequest('/session/patient/custom_survey/create', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        ...surveyData
      }),
    });
    return response.json();
  },

  // Delete custom survey
  deleteCustomSurvey: async (patientId: string, surveyId: string) => {
    const response = await apiRequest('/session/patient/custom_survey/delete', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        survey_id: surveyId
      }),
    });
    return response.json();
  },

  // Update custom survey
  updateCustomSurvey: async (patientId: string, surveyId: string, surveyData: {
    name?: string;
    questions?: { prompt: string; data_type: string }[];
  }) => {
    const response = await apiRequest('/session/patient/custom_survey/update', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        survey_id: surveyId,
        ...surveyData
      }),
    });
    return response.json();
  },

  // Check if patient ID is available and create patient-user link if needed
  checkPatientIdAvailable: async (patientId: string) => {
    const response = await apiRequest('/is_patient_id_available_for_user', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
    return response.json();
  },

  // Log patient action/activity (explicit time logging with notes)
  logAction: async (actionData: {
    action: string;
    action_begin_ts: string;
    action_user: string;
    patient_id: string;
    action_end_ts?: string | null;
    details?: object | null;
    selected_user_id?: string | null;
  }) => {
    const response = await apiRequest('/session/patient/action/log', {
      method: 'POST',
      body: JSON.stringify(actionData),
    });
    return response.json();
  },

  // Log dwell time (implicit time tracking)
  logDwellTime: async (dwellData: {
    action_begin_ts: string;
    patient_id: string;
    action_end_ts: string;
  }) => {
    const response = await apiRequest('/session/patient/action/log_dwell', {
      method: 'POST',
      body: JSON.stringify(dwellData),
    });
    return response.json();
  },

  // Start VAPI AI voice call for survey collection
  startVapiCall: async (
    patientId: string,
    phoneNumber: string,
    patientName: string,
    organizationName?: string,
    surveyName?: string,
    surveyId?: string,
    language?: string
  ) => {
    const response = await apiRequest('/session/patient/contact_action/vapi_call', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        phone_number: phoneNumber,
        patient_name: patientName,
        organization_name: organizationName,
        survey_name: surveyName || 'Blood Pressure Check',
        survey_id: surveyId,
        language: language
      }),
    });
    return response.json();
  },

  // Persist task completion status
  persistTaskCompletion: async (payload: {
    patient_id: string;
    disease: string;
    uploaded_ts: string;
    task: string;
    normal: boolean;
    occurrence_id: number;
    completed_at: string | null;
    completion_user: string | null;
  }) => {
    const response = await apiRequest('/session/patient/care_plan_definition/task/persist_occurrence_completion', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.json();
  },

  // Get ICD-10 codes for a patient
  getICD10Codes: async (patientId: string) => {
    const response = await apiRequest('/session/patient/care_plan/icd10_codes', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId
      }),
    });
    return response.json();
  },

  // Get most recent vitals for a patient
  getVitals: async (patientId: string) => {
    const response = await apiRequest('/session/patient/vitals', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId })
    });
    return response.json();
  },

  // PHA-1142: every vital / lab reading (EHR + entered), with acknowledgment status and the
  // readings the current / draft care plan's goal targets were calculated from.
  getObservationHistory: async (patientId: string): Promise<ObservationHistory> => {
    const response = await apiRequest('/session/patient/observations/history', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
    return response.json();
  },

  // PHA-1141: the patient's undecided EHR changes, every recorded decision, and the latest EHR
  // import batch (the Smart Hub's Re-generate reviews from it without a second EHR pull).
  getEhrChanges: async (patientId: string): Promise<PatientEhrChanges> => {
    const response = await apiRequest('/session/patient/ehr_changes', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
    return response.json();
  },

  // PHA-1141 / PHA-1142: accept or reject individual EHR changes. Accepted problem / medication
  // changes are written into the chart's lists; a sync run clears from the Smart Hub once every
  // item in it is decided.
  acknowledgeEhrChanges: async (
    patientId: string,
    decisions: EhrChangeDecision[],
  ): Promise<{ acknowledged: number; skipped: number; lists_updated: string[]; closed_runs: number[] }> => {
    const response = await apiRequest('/session/patient/ehr_changes/acknowledge', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId, decisions }),
    });
    return response.json();
  },

  // Get previously uploaded document names for a patient
  getDocumentNames: async (patientId: string) => {
    const response = await apiRequest('/session/patient/document_names', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId })
    });
    return response.json();
  },

  // Import patient from EHR by MRN.
  // PHA-958: phoneNumber is the clinician-supplied fallback for a chart the EHR holds no
  // phone for. The backend prefers the EHR's own number and only falls back to this one,
  // so sending it always is safe; when both are absent the response comes back 200 with
  // needs_phone_number rather than as an error.
  // updateIfExists re-imports onto an MRN that is already in Phanova, overwriting the
  // patient's demographics with the EHR's and adding fresh candidate items. Send it only
  // after the clinician has confirmed the "Patient Already Exists" dialog — without it the
  // backend rejects a duplicate MRN, which is what keeps the confirmation meaningful.
  importFromEhr: async (patientId: string, phoneNumber?: string, updateIfExists?: boolean) => {
    const body: Record<string, string | boolean> = { patient_id: patientId };
    const trimmedPhone = phoneNumber?.trim();
    if (trimmedPhone) {
      body.phone_number = trimmedPhone;
    }
    if (updateIfExists) {
      body.update_if_exists = true;
    }
    const response = await apiRequest('/session/patient/import_from_ehr', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json();
  },

  // Cancel EHR import (deletes the FHIR-sourced patient)
  cancelImportFromEhr: async (patientId: string) => {
    const response = await apiRequest('/session/patient/cancel_import_from_ehr', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
    return response.json();
  },

  // PHA-397: trigger an ad-hoc EHR refresh for an existing patient. Async —
  // backend spawns a daemon thread, writes an ehr_sync_runs row, and surfaces
  // the result in the Smart Hub. Returns 202 {status:'triggered'} on success.
  // Non-OK responses throw via apiRequest with `err.errorData.status` set
  // (409 already-running, 403 no-access, 400 validation).
  refreshFromEhr: async (params: { patient_id: string; organization_id?: string }) => {
    const response = await apiRequest('/session/patient/refresh_from_ehr', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return response.json();
  },

  // Check if the given org has EHR integration. Pass an explicit org id when the
  // caller knows which clinic is selected — omitting it falls back to whatever org
  // the BFF session holds, which lags the org switcher.
  checkOrgEhrIntegration: async (organizationId?: string) => {
    const response = await apiRequest('/session/organization/has_ehr_integration', {
      method: 'POST',
      body: JSON.stringify(organizationId ? { organization_id: organizationId } : {}),
    });
    return response.json();
  },

  // Check if the given org has Voice AI enabled (org id optional, as above)
  checkOrgVoiceEnabled: async (organizationId?: string) => {
    const response = await apiRequest('/session/organization/voice_enabled', {
      method: 'POST',
      body: JSON.stringify(organizationId ? { organization_id: organizationId } : {}),
    });
    return response.json();
  },

  // PHA-1225: the Program Confirmation Form (consent by text). Without a language the form
  // goes in the patient's preferred language.
  sendConsentRequest: async (
    patientId: string,
    language?: string,
  ): Promise<{ consent_request_id: string; send_status: string; language?: string }> => {
    const response = await apiRequest('/session/patient/consent/send', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId, ...(language ? { language } : {}) }),
    });
    return response.json();
  },

  getConsentPdf: async (consentRequestId: string): Promise<{ filename: string; pdf_base64: string }> => {
    const response = await apiRequest('/session/patient/consent/pdf', {
      method: 'POST',
      body: JSON.stringify({ consent_request_id: consentRequestId }),
    });
    return response.json();
  },

  listConsentRequests: async (patientId: string): Promise<ConsentRequestList> => {
    const response = await apiRequest('/session/patient/consent/list', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
    return response.json();
  },

  // 409 = this answer is already in the EHR; the caller asks before sending another copy.
  pushConsentToEhr: async (consentRequestId: string, force = false) => {
    try {
      const response = await apiRequest('/session/patient/consent/push_to_ehr', {
        method: 'POST',
        body: JSON.stringify({ consent_request_id: consentRequestId, force }),
      });
      return response.json();
    } catch (err: any) {
      if (err?.errorData?.status === 409) {
        err.alreadySent = true;
      }
      throw err;
    }
  },

  // PHA-626: the onboarding-script wording of the patient's clinic (or its parent network).
  getOnboardingScriptTemplate: async (
    organizationId: string,
  ): Promise<{ template: OnboardingScriptTemplate | null; clinic_name: string | null }> => {
    const response = await apiRequest('/session/organization/onboarding_script_template', {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId }),
    });
    return response.json();
  },

  // PHA-626: the AI-written clinician brief shown above the onboarding script.
  getClinicianBrief: async (patientId: string): Promise<ClinicianBrief> => {
    const response = await apiRequest('/session/patient/clinician_brief', {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
    return normalizeClinicianBrief(await response.json());
  },

  // PHA-795: Check if the given org (ancestor-aware) has Voice of Patient enabled
  getOrgVoiceOfPatientEnabled: async (organizationId: string) => {
    const response = await apiRequest('/session/organization/voice_of_patient_enabled', {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId }),
    });
    return response.json();
  },

  // PHA-866: Check if the given org (ancestor-aware) has "no target dates" enabled
  getOrgNoTargetDatesEnabled: async (organizationId: string) => {
    const response = await apiRequest('/session/organization/no_target_dates_enabled', {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId }),
    });
    return response.json();
  },

  // PHA-1031: Check if the given org (ancestor-aware) has the Smart Hub hidden
  getOrgHideSmartHubEnabled: async (organizationId: string) => {
    const response = await apiRequest('/session/organization/hide_smart_hub_enabled', {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId }),
    });
    return response.json();
  },

  // PHA-1080: the clinic's care plan review cycle in months (nearest-ancestor, default 6).
  // Sets the default in the approval wizard's review-date field, which is what becomes the
  // "Care Plan Review Date" on the patient list and the printed plan.
  getOrgCarePlanReviewMonths: async (organizationId: string) => {
    const response = await apiRequest('/session/organization/care_plan_review_months', {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId }),
    });
    return response.json();
  },

  // PHA-1187: is this patient's number flagged as unable to receive SMS (and why)?
  getPatientSmsReachability: async (
    patientId: string,
    organizationId?: string
  ): Promise<SmsReachability> => {
    const response = await apiRequest('/session/patient/sms_reachability', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId,
        ...(organizationId ? { organization_id: organizationId } : {}),
      }),
    });
    return response.json();
  },

  // PHA-1187: the clinic's work list — every visible patient whose number Twilio reported
  // as undeliverable. Omit the org id (or pass 'ALL') for every clinic the user can see.
  getSmsUndeliverablePatients: async (
    organizationId?: string
  ): Promise<{ patients: SmsUndeliverablePatient[]; count: number }> => {
    const response = await apiRequest('/session/organization/sms_undeliverable_patients', {
      method: 'POST',
      body: JSON.stringify(organizationId ? { organization_id: organizationId } : {}),
    });
    return response.json();
  },

  // Persist goal achievement status
  persistGoalAchievement: async (payload: {
    patient_id: string;
    disease: string;
    uploaded_ts: string;
    measure: string;
    achieved: boolean;
    achieved_date: string;
    attesting_user: string | null;
  }) => {
    const response = await apiRequest('/session/patient/care_plan_definition/goal/persist_achievement', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.json();
  },
};

// Billing/Reporting API
export const billingApi = {
  // Get monthly billing report
  getMonthlyReport: async (patientId?: string | null, fromDate?: string, toDate?: string, organizationId?: string | null, signal?: AbortSignal) => {
    const response = await apiRequest('/ccm/billing/activities_report/monthly', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId || null,
        from_date: fromDate || null,
        to_date: toDate || null,
        ...(organizationId ? { organization_id: organizationId } : {}),
      }),
      signal,
    });
    return response.json();
  },

  // PHA-1104: whether "Push Billing Report to EMR" should appear for this patient, and
  // whether the period on screen has already been filed.
  getBillingReportEhrPushStatus: async (
    patientId: string,
    fromDate?: string,
    toDate?: string,
  ) => {
    const query = new URLSearchParams({ patient_id: patientId });
    if (fromDate) query.set('from_date', fromDate);
    if (toDate) query.set('to_date', toDate);
    const response = await apiRequest(
      `/session/patient/billing_report/ehr_push_status?${query.toString()}`
    );
    return response.json();
  },

  // PHA-1104: file the combined billing report PDF into the EHR.
  //
  // Multipart for the same reason as pushCarePlanToEhr: what the browser holds is a Blob.
  // `headers: {}` is deliberate and load-bearing — apiRequest defaults to
  // 'Content-Type: application/json', and leaving that in place stops fetch generating the
  // multipart boundary, so the upload arrives unparseable.
  pushBillingReportToEhr: async (
    patientId: string,
    pdf: Blob,
    filename: string,
    fromDate: string,
    toDate: string,
    force = false,
  ) => {
    const form = new FormData();
    form.append('patient_id', patientId);
    form.append('from_date', fromDate);
    form.append('to_date', toDate);
    form.append('force', force ? 'true' : 'false');
    form.append('document', pdf, filename);

    let response: Response;
    try {
      response = await apiRequest('/session/patient/billing_report/push_to_ehr', {
        method: 'POST',
        headers: {},
        body: form,
      });
    } catch (err: any) {
      // apiRequest THROWS on any non-ok status, so it never hands back a non-ok Response —
      // a `if (!response.ok)` check here would be dead code (the PHA-983 bug). The 409 has
      // to be recovered from the thrown error instead, and the status lives on `errorData`.
      //
      // 409 means this period is already filed. It is a confirmable state, not a failure:
      // the caller turns it into a "send another copy?" prompt.
      if (err?.errorData?.status === 409) {
        err.alreadySent = true;
        err.status = 409;
      }
      throw err;
    }
    return response.json();
  },

  // Get action report for a patient
  // PHA-891: forward the date range so the (now collapsed/interval-unioned)
  // detail rows are selected server-side at the same point billing filters —
  // the client must not re-filter merged rows by their union start.
  getActionReport: async (patientId?: string | null, organizationId?: string | null, fromDate?: string, toDate?: string) => {
    const response = await apiRequest('/session/patient/action/report', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId || null,
        from_date: fromDate || null,
        to_date: toDate || null,
        ...(organizationId ? { organization_id: organizationId } : {}),
      }),
    });
    return response.json();
  },

  // PHA-891 (addi, 2026-09-01): the chart's Event Log — one row per real event
  // with its own timestamp, never the collapsed rows getActionReport serves.
  getEventLog: async (patientId?: string | null, organizationId?: string | null, fromDate?: string, toDate?: string) => {
    const response = await apiRequest('/session/patient/action/event_log', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId || null,
        from_date: fromDate || null,
        to_date: toDate || null,
        ...(organizationId ? { organization_id: organizationId } : {}),
      }),
    });
    return response.json();
  },

  // PHA-629: row-level patient engagement (survey submissions + device readings)
  // behind the billing report's Patient Activities column. Same date-range
  // contract as getActionReport — the backend selects rows with the identical
  // predicates billing uses, so the counts tie out per (patient, month).
  getPatientActivityReport: async (patientId?: string | null, organizationId?: string | null, fromDate?: string, toDate?: string) => {
    const response = await apiRequest('/ccm/billing/patient_activity/report', {
      method: 'POST',
      body: JSON.stringify({
        patient_id: patientId || null,
        from_date: fromDate || null,
        to_date: toDate || null,
        ...(organizationId ? { organization_id: organizationId } : {}),
      }),
    });
    return response.json();
  },
};

// PHA-441 Smart Hub API
export const smartHubApi = {
  taskBar: async (signal?: AbortSignal, organizationId?: string) => {
    const response = await apiRequest('/session/smart_hub/task_bar', {
      method: 'POST',
      body: JSON.stringify(organizationId ? { organization_id: organizationId } : {}),
      signal,
    });
    return response.json();
  },
  billingGaps: async (signal?: AbortSignal, organizationId?: string): Promise<{ status: number; data: any }> => {
    const response = await apiRequest('/session/smart_hub/billing_gaps', {
      method: 'POST',
      body: JSON.stringify(organizationId ? { organization_id: organizationId } : {}),
      signal,
    });
    // Surface the status so callers can treat 403 as "user lacks billing role"
    // (not a real error) and silently hide the strip.
    let data: any = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    return { status: response.status, data };
  },
  acknowledgeAnswer: async (payload: {
    submission_id: string;
    prompt: string;
    dwell_seconds: number;
  }) => {
    const response = await apiRequest('/session/patient/answer/acknowledge', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.json();
  },
  acknowledgeReading: async (payload: { reading_id: number; dwell_seconds: number }) => {
    const response = await apiRequest('/session/patient/reading/acknowledge', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.json();
  },
  acknowledgeSubmission: async (payload: {
    submission_id: string;
    // Explicit prompt list — only ack the answers that were visible to the
    // user; cross-bucket rows in the same submission stay unack.
    prompts: string[];
    dwell_seconds: number;
  }): Promise<{ submission_id: string; acknowledged: number }> => {
    const response = await apiRequest('/session/patient/submission/acknowledge_all', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.json();
  },
  // PHA-510: idempotent ack for an ehr_sync_runs row. Auto-fired when the
  // clinician clicks the "EHR changes pending review" Smart Hub row to
  // navigate to CarePlanApproval; or fired explicitly via AckDrawer for
  // 'no_changes_applicable' rows that have no draft to approve.
  acknowledgeEhrChange: async (payload: { ehr_sync_run_id: number; dwell_seconds: number }) => {
    const response = await apiRequest('/session/smart_hub/ehr_change/ack', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.json();
  },
  // PHA-678: bulk acknowledge mixed item types for one patient.
  bulkAcknowledge: async (payload: {
    patient_id: string;
    items: Array<
      | { kind: 'reading'; reading_id: number }
      | { kind: 'answer'; submission_id: string; prompt: string }
      | { kind: 'ehr_change'; ehr_sync_run_id: number }
    >;
  }): Promise<{ acknowledged: number }> => {
    const response = await apiRequest('/session/smart_hub/bulk_acknowledge', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.json();
  },
};

// PHA-567 SmartHub AI API — additive layer over the rule-based feed.
// All routes are GET/POST through the BFF; org id auto-injected from session.
export const aiInsightsApi = {
  list: async (signal?: AbortSignal, organizationId?: string) => {
    const qs = organizationId ? `?org_id=${encodeURIComponent(organizationId)}` : '';
    const response = await apiRequest(`/session/smart_hub/ai_insights${qs}`, {
      method: 'GET',
      signal,
    });
    return response.json();
  },
  get: async (
    patientId: string,
    opts: { includeHistory?: boolean; organizationId?: string } = {},
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams();
    if (opts.organizationId) params.set('org_id', opts.organizationId);
    if (opts.includeHistory) params.set('include', 'history');
    const qs = params.toString();
    const path = `/session/smart_hub/ai_insights/${encodeURIComponent(patientId)}${qs ? `?${qs}` : ''}`;
    const response = await apiRequest(path, { method: 'GET', signal });
    return response.json();
  },
  refresh: async (patientId: string, organizationId?: string) => {
    const response = await apiRequest(
      `/session/smart_hub/ai_insights/${encodeURIComponent(patientId)}/refresh`,
      {
        method: 'POST',
        body: JSON.stringify(organizationId ? { organization_id: organizationId } : {}),
      },
    );
    return { status: response.status, data: await response.json() };
  },
  feedback: async (
    insightId: number,
    payload: { rating: 'useful' | 'not_useful' | 'wrong'; reason?: string },
  ) => {
    const response = await apiRequest(
      `/session/smart_hub/ai_insights/${insightId}/feedback`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
    return response.json();
  },
};

// Organization API
export const organizationApi = {
  list: async () => {
    const response = await apiRequest('/session/organization/list', { method: 'GET' });
    return response.json();
  },
  getCurrent: async () => {
    const response = await apiRequest('/session/organization/current', { method: 'GET' });
    return response.json();
  },
  getSupportedLanguages: async (organizationId?: string) => {
    const response = await apiRequest('/session/organization/supported_languages', {
      method: 'POST',
      body: JSON.stringify(organizationId ? { organization_id: organizationId } : {}),
    });
    return response.json();
  },
  upsertSupportedLanguage: async (language: string, smsTemplate?: string, organizationId?: string) => {
    const response = await apiRequest('/session/organization/supported_language/upsert', {
      method: 'POST',
      body: JSON.stringify({ language, sms_message_template: smsTemplate, ...(organizationId ? { organization_id: organizationId } : {}) }),
    });
    return response.json();
  },
  deleteSupportedLanguage: async (language: string, organizationId?: string) => {
    const response = await apiRequest('/session/organization/supported_language/delete', {
      method: 'POST',
      body: JSON.stringify({ language, ...(organizationId ? { organization_id: organizationId } : {}) }),
    });
    return response.json();
  },
  // PHA-723: per-clinic supported programs (APCM / CCM / RPM).
  getSupportedPrograms: async (organizationId?: string) => {
    const response = await apiRequest('/session/organization/supported_programs', {
      method: 'POST',
      body: JSON.stringify(organizationId ? { organization_id: organizationId } : {}),
    });
    return response.json();
  },
  upsertSupportedProgram: async (program: string, organizationId?: string) => {
    const response = await apiRequest('/session/organization/supported_program/upsert', {
      method: 'POST',
      body: JSON.stringify({ program, ...(organizationId ? { organization_id: organizationId } : {}) }),
    });
    return response.json();
  },
  deleteSupportedProgram: async (program: string, organizationId?: string) => {
    const response = await apiRequest('/session/organization/supported_program/delete', {
      method: 'POST',
      body: JSON.stringify({ program, ...(organizationId ? { organization_id: organizationId } : {}) }),
    });
    return response.json();
  },
  // PHA-723: hierarchy-resolved supported programs for an org, used to hide
  // program-specific columns/tabs. Returns { programs: string[] }.
  getEffectiveSupportedPrograms: async (organizationId?: string) => {
    const response = await apiRequest('/session/organization/supported_programs_effective', {
      method: 'POST',
      body: JSON.stringify(organizationId ? { organization_id: organizationId } : {}),
    });
    return response.json();
  },
};

export const schedulerApi = {
  getUpcomingItems: async (filters: {
    days_ahead?: number;
    start_date?: string;
    end_date?: string;
    item_types?: string[];
    patient_id?: string | null;
    status_filter?: string[];
  }) => {
    const response = await apiRequest('/api/scheduler/upcoming', {
      method: 'POST',
      body: JSON.stringify(filters),
    });
    return response.json();
  },

  getSummary: async (filters: {
    days_ahead?: number;
    start_date?: string;
    end_date?: string;
    item_types?: string[];
    patient_id?: string | null;
  }) => {
    const response = await apiRequest('/api/scheduler/summary', {
      method: 'POST',
      body: JSON.stringify(filters),
    });
    return response.json();
  },
};

export const schedulerTestApi = {
  getArbitraryNow: async () => {
    const response = await apiRequest('/api/scheduler/test/arbitrary_now', { method: 'GET' });
    return response.json();
  },

  setArbitraryNow: async (value: string) => {
    const response = await apiRequest('/api/scheduler/test/arbitrary_now', {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
    return response.json();
  },

  getEligiblePatients: async () => {
    const response = await apiRequest('/api/scheduler/test/eligible_patients', { method: 'GET' });
    return response.json();
  },

  triggerSurvey: async (payload: {
    patient_id: string;
    survey_id: string;
    arbitrary_now: string;
    phone_number_override?: string;
    contact_method?: string;
  }) => {
    const response = await apiRequest('/api/scheduler/test/trigger', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.json();
  },
  clearClaim: async (occurrence_id: number) => {
    const response = await apiRequest('/api/scheduler/test/clear_claim', {
      method: 'POST',
      body: JSON.stringify({ occurrence_id }),
    });
    return response.json();
  },
};

export const organizationsAdminApi = {
  listContactExclusions: async () => {
    const response = await apiRequest('/api/organizations/contact-exclusions', {
      method: 'GET',
    });
    return response.json();
  },

  listExcludedDates: async (orgId: string | number) => {
    const response = await apiRequest(`/api/organizations/${orgId}/contact-excluded-dates`, {
      method: 'GET',
    });
    return response.json();
  },

  updateExcludedWeekdays: async (orgId: string | number, weekdays: string[]) => {
    const response = await apiRequest(`/api/organizations/${orgId}/contact-excluded-weekdays`, {
      method: 'PUT',
      body: JSON.stringify({ weekdays }),
    });
    return response.json();
  },

  addExcludedDate: async (orgId: string | number, payload: { exclusion: string; reason?: string | null }) => {
    const response = await apiRequest(`/api/organizations/${orgId}/contact-excluded-dates`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response.json();
  },

  deleteExcludedDate: async (orgId: string | number, exclusion: string) => {
    const response = await apiRequest(`/api/organizations/${orgId}/contact-excluded-dates`, {
      method: 'DELETE',
      body: JSON.stringify({ exclusion }),
    });
    return response.json();
  },
};

// PHA-406: Survey settings per organization
export const surveySettingsApi = {
  getSettings: async (orgId: string) => {
    const response = await apiRequest(`/admin/organizations/${orgId}/survey-settings`);
    return response.json();
  },
  updateSettings: async (orgId: string, data: {
    survey_default_cron?: string;
    categories?: { [key: string]: boolean };
  }) => {
    const response = await apiRequest(`/admin/organizations/${orgId}/survey-settings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },
};

// PHA-406 V2: Survey templates per organization
export const surveyTemplatesApi = {
  getTemplates: async (orgId: string) => {
    const response = await apiRequest(`/admin/organizations/${orgId}/survey-templates`);
    return response.json();
  },
  saveTemplates: async (orgId: string, templates: any[]) => {
    const response = await apiRequest(`/admin/organizations/${orgId}/survey-templates`, {
      method: 'PUT',
      body: JSON.stringify({ templates }),
    });
    return response.json();
  },
};

export const clinicApi = {
  // Settings
  getSettings: async () => {
    const response = await apiRequest(`/clinic/settings`, {
      method: 'GET',
    });
    return response.json();
  },

  // PHA-1225: Program Confirmation Form wording for one language; '' = back to the fallback
  // (English: Phanova's default; another language: the clinic's English wording).
  updateConsentSettings: async (data: {
    language?: string;
    consentSmsTemplate?: string;
    consentFormText?: string;
    consentQuestion?: string;
  }) => {
    const response = await apiRequest(`/clinic/settings/consent`, {
      method: 'PUT',
      body: JSON.stringify({
        language: data.language ?? 'en-US',
        consent_sms_template: data.consentSmsTemplate,
        consent_form_text: data.consentFormText,
        consent_question: data.consentQuestion,
      }),
    });
    return response.json();
  },

  // PHA-626: the clinic's onboarding-script wording; null = back to the built-in script.
  updateOnboardingScript: async (template: OnboardingScriptTemplate | null) => {
    const response = await apiRequest(`/clinic/settings/onboarding_script`, {
      method: 'PUT',
      body: JSON.stringify({ template }),
    });
    return response.json();
  },

  updateSettings: async (data: { name?: string; logoPath?: string; voicePhoneNumber?: string; smsPhoneNumber?: string; smsMessageTemplate?: string; rpmReadingsSmsTemplate?: string; smsAutoReplyEnabled?: boolean; smsAutoReplyPhoneNumber?: string; smsAutoReplyTemplate?: string; tellihealthWebhookToken?: string; voiceEnabled?: boolean; welcomeSmsEnabled?: boolean; welcomeSmsTemplate?: string }) => {
    const response = await apiRequest(`/clinic/settings`, {
      method: 'PUT',
      body: JSON.stringify({
        name: data.name,
        logo_path: data.logoPath,
        voice_phone_number: data.voicePhoneNumber,
        sms_phone_number: data.smsPhoneNumber,
        sms_message_template: data.smsMessageTemplate,
        rpm_readings_sms_template: data.rpmReadingsSmsTemplate ?? null,
        // PHA-1010: ?? null, not `|| undefined` — undefined is the BFF's "leave unchanged"
        // signal, so a cleared callback number has to arrive as an explicit null.
        sms_auto_reply_enabled: data.smsAutoReplyEnabled,
        sms_auto_reply_phone_number: data.smsAutoReplyPhoneNumber ?? null,
        sms_auto_reply_template: data.smsAutoReplyTemplate ?? null,
        tellihealth_webhook_token: data.tellihealthWebhookToken,
        voice_enabled: data.voiceEnabled,
        welcome_sms_enabled: data.welcomeSmsEnabled,
        welcome_sms_template: data.welcomeSmsTemplate ?? null,
      }),
    });
    return response.json();
  },

  // Users
  getUsers: async () => {
    const response = await apiRequest(`/clinic/users`, {
      method: 'GET',
    });
    return response.json();
  },

  inviteUser: async (email: string, roleIds: string[], useSSO: boolean = false) => {
    const response = await apiRequest(`/clinic/users`, {
      method: 'POST',
      body: JSON.stringify({ email, roleIds, use_sso: useSSO }),
    });
    return response.json();
  },

  createInviteLink: async (userId: string) => {
    const response = await apiRequest(`/clinic/users/${userId}/invite`, {
      method: 'POST',
    });
    return response.json();
  },

  updateUserRoles: async (userId: string, roleIds: string[]) => {
    const response = await apiRequest(`/clinic/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ roleIds }),
    });
    return response.json();
  },

  removeUser: async (userId: string) => {
    const response = await apiRequest(`/clinic/users/${userId}`, {
      method: 'DELETE',
    });
    return response.json();
  },
};

/** PHA-1187: Twilio reported the patient's number as unable to receive SMS. */
export interface SmsReachability {
  patient_id?: string;
  sms_undeliverable: boolean;
  /** Twilio error code: 30003 unreachable handset, 30005 unknown/inactive, 30006 landline. */
  error_code?: string | null;
  error_label?: string | null;
  reason?: string | null;
  suppressed_since?: string | null;
  voice_enabled?: boolean;
  /** The scheduler sends this patient's surveys by Voice AI instead of SMS. */
  surveys_route_to_voice?: boolean;
  /** PHA-790: SMS consent. 'unsubscribed' = patient replied STOP; only they can undo it (START). */
  sms_subscription_status?: 'subscribed' | 'unsubscribed' | null;
  /** Unsubscribed: STOP date (or first refused send); subscribed after an opt-out: resume date. */
  sms_status_changed_at?: string | null;
  sms_status_source?: string | null;
}

export interface SmsUndeliverablePatient extends SmsReachability {
  patient_id: string;
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
  organization_id?: string | null;
  organization_name?: string | null;
}

export interface AdminVoiceCallRow {
  id: string;
  mrn: string;
  patientName: string | null;
  clinic: string | null;
  organizationId: string | null;
  initiatedAt: string;
  durationSeconds: number | null;
  outcome: string | null;
  outcomeSource: string | null;
  endedReason: string | null;
  endedAbruptly: boolean;
  hasVoicemail: boolean;
  validationStatus: string | null;
  answeredCount: number;
  questionCount: number;
  offScript: Array<{ statement: string | null; timestamp?: string }>;
  // Deliberately a boolean, never a URL — playback goes through the streaming
  // route so the S3 link stays presigned and server-side.
  hasRecording: boolean;
}

export interface AdminVoiceCallsResponse {
  rows: AdminVoiceCallRow[];
  summary: {
    total: number;
    byOutcome: Record<string, number>;
    completionRate: number | null;
    // PHA-1272: true when CI/Voice Lab/test-patient calls were left out.
    testCallsExcluded?: boolean;
    // PHA-1270: null until the latency columns exist in this environment.
    turnLatency?: { p50Ms: number | null; p90Ms: number | null; measured: number; overBudget: number; budgetMs: number } | null;
  };
  page: number;
  pageSize: number;
  hasMore: boolean;
}

// PHA-1281 voice line queue: VAPI lines in use, and scheduled calls waiting for one.
export interface AdminVoiceQueueResponse {
  lines: { onAir: number; scheduledLimit: number; totalLimit: number };
  // null until the voice_call_queue migration has run in this environment.
  queue: {
    queued: number;
    oldestQueuedSince: string | null;
    byClinic: Array<{
      organizationId: string | null;
      organizationName: string | null;
      queued: number;
      oldestQueuedSince: string | null;
    }>;
  } | null;
}

export const adminVoiceApi = {
  queueStatus: async (): Promise<AdminVoiceQueueResponse> => {
    const response = await apiRequest('/api/voice/admin/voice-calls/queue');
    return response.json();
  },

  listCalls: async (params: Record<string, string | undefined>): Promise<AdminVoiceCallsResponse> => {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '') as [string, string][]
    ).toString();
    const response = await apiRequest(`/api/voice/admin/voice-calls${query ? `?${query}` : ''}`);
    if (!response.ok) throw new Error('Failed to load voice calls');
    return response.json();
  },

  listClinics: async (): Promise<{ clinics: Array<{ id: string; name: string | null }> }> => {
    const response = await apiRequest('/api/voice/admin/voice-calls/clinics');
    if (!response.ok) throw new Error('Failed to load clinics');
    return response.json();
  },
};

// PHA-1223: GET /admin/ehr_push/issues
export type EhrUploadIssueType = 'failed' | 'pending' | 'not_uploaded' | 'resolved';
export type EhrUploadAutoRetry = {
  transient: boolean;
  attempts: number;
  max_attempts: number;
  scheduled: boolean;
  next_at: string | null;
  exhausted: boolean;
  last_attempt_at?: string | null;
  /** Why a failed push is NOT scheduled for retry (a label only); null when it is, or it did not fail. */
  not_retried_reason?: EhrUploadNotRetriedReason | null;
};
export type EhrUploadNotRetriedReason =
  | 'permanent'
  | 'disabled'
  | 'exhausted'
  | 'no_archive'
  | 'too_old'
  | 'no_credential'
  | 'resent'
  | 'not_eligible';
/** One push of the patient's current care plan, oldest first. */
export type EhrUploadPushHistoryItem = {
  push_id: string | null;
  requested_at: string | null;
  confirmed_at: string | null;
  status: string | null;
  error: string | null;
  folder_name: string | null;
  automatic: boolean;
  pushed_by_name: string | null;
  auto_retries: number;
  last_auto_retry_at: string | null;
  /** Each earlier failure an automatic retry replaced. */
  auto_retry_failures: Array<{ error: string | null; failed_at: string | null }>;
};
export type EhrUploadIssueRow = {
  issue: EhrUploadIssueType;
  organization_id: string;
  clinic: string | null;
  mrn: string;
  approved_at: string | null;
  push_id: string | null;
  status: string | null;
  error: string | null;
  requested_at: string | null;
  confirmed_at: string | null;
  automatic: boolean;
  pushed_by_name: string | null;
  attempts: number;
  pushes_filed?: number;
  pushes_failed?: number;
  in_ehr: boolean;
  auto_retry: EhrUploadAutoRetry;
  history?: EhrUploadPushHistoryItem[];
};
export type EhrUploadIssuesResponse = {
  since: string;
  days: number;
  rows: EhrUploadIssueRow[];
  by_clinic: Array<{ organization_id: string; clinic: string | null } & Record<EhrUploadIssueType, number>>;
  totals: Record<EhrUploadIssueType, number>;
  clinics: Array<{ id: string; name: string | null }>;
  auto_retry_enabled: boolean;
};

export const adminApi = {
  // Users (create/archive)
  createUser: async (data: any) => {
    const response = await apiRequest(`/admin/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  createInviteLink: async (userId: string) => {
    const response = await apiRequest(`/admin/users/${userId}/invite`, {
      method: 'POST',
    });
    return response.json();
  },

  updateUser: async (userId: string, data: any) => {
    const response = await apiRequest(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Clinics
  getOrganizations: async () => {
    const response = await apiRequest(`/admin/organizations`, {
      method: 'GET',
    });
    return response.json();
  },

  // PHA-958: names of the entries in the shared Cobalt credentials, for mapping an
  // eClinicalWorks clinic to its account. Names only — the backend never returns the
  // credentials themselves. Answers { keys: string[], error?: string }.
  listCobaltCredentialKeys: async () => {
    const response = await apiRequest(`/admin/cobalt/credential_keys`, {
      method: 'GET',
    });
    return response.json();
  },

  // PHA-1300: names of the entries in the shared ECW_FHIR_CREDENTIALS secret, for mapping an
  // eClinicalWorks clinic to a direct-FHIR practice. Names (+ non-secret practice_id) only —
  // the backend never returns the credentials themselves.
  // Answers { keys: string[], accounts: [{key, practice_id}], error?: string }.
  listEcwCredentialKeys: async () => {
    const response = await apiRequest(`/admin/ecw/credential_keys`, {
      method: 'GET',
    });
    return response.json();
  },

  // PHA-983: live lookups that populate the care-plan-push pickers for a given Cobalt
  // account, so an admin selects a real facility/staff member instead of typing an opaque
  // id. Answer { locations: [{id, ehr_id, name, status}] } / { providers: [...] }.
  listCobaltLocations: async (credentialKey: string) => {
    const response = await apiRequest(
      `/admin/cobalt/locations?credential_key=${encodeURIComponent(credentialKey)}`,
      { method: 'GET' }
    );
    return response.json();
  },

  // PHA-1202: RingCentral accounts (names + whether credentials are filled in) and the
  // SMS-enabled numbers of one account. Answer { accounts: [{key, configured}], error? } /
  // { numbers: [{phone_number, usage_type, label}], error? }.
  listRingCentralCredentialKeys: async () => {
    const response = await apiRequest(`/admin/ringcentral/credential_keys`, { method: 'GET' });
    return response.json();
  },

  listRingCentralSmsNumbers: async (credentialKey: string) => {
    const response = await apiRequest(
      `/admin/ringcentral/sms_numbers?credential_key=${encodeURIComponent(credentialKey)}`,
      { method: 'GET' }
    );
    return response.json();
  },

  listCobaltProviders: async (credentialKey: string) => {
    const response = await apiRequest(
      `/admin/cobalt/providers?credential_key=${encodeURIComponent(credentialKey)}`,
      { method: 'GET' }
    );
    return response.json();
  },

  createOrganization: async (data: any) => {
    const response = await apiRequest(`/admin/organizations`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateOrganization: async (id: string, data: any) => {
    const response = await apiRequest(`/admin/organizations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  // Upload a clinic logo file. Returns { logo_path } (a same-origin proxy URL) to persist via
  // updateOrganization. Uses a bare fetch so the browser sets the multipart boundary itself —
  // apiRequest would force a JSON Content-Type and break the upload.
  uploadOrganizationLogo: async (id: string, file: File): Promise<{ logo_path: string }> => {
    const form = new FormData();
    form.append('logo', file);
    const response = await fetch(getApiUrl(`/admin/organizations/${id}/logo`), {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!response.ok) {
      let message = 'Failed to upload logo';
      try { const e = await response.json(); message = e.error || message; } catch { /* ignore */ }
      throw new Error(message);
    }
    return response.json();
  },

  // Import a clinic logo from an external URL (fetched and stored server-side, so it renders
  // reliably on the printed care plan despite browser cross-origin fetch restrictions).
  importOrganizationLogoFromUrl: async (id: string, sourceUrl: string): Promise<{ logo_path: string }> => {
    const response = await apiRequest(`/admin/organizations/${id}/logo`, {
      method: 'POST',
      body: JSON.stringify({ source_url: sourceUrl }),
    });
    return response.json();
  },

  // Users
  getUsers: async (params?: { organization_id?: string; search?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.organization_id) {
      searchParams.set('organization_id', params.organization_id);
    }
    if (params?.search) {
      searchParams.set('search', params.search);
    }
    const queryString = searchParams.toString();
    const response = await apiRequest(`/admin/users${queryString ? `?${queryString}` : ''}`, {
      method: 'GET',
    });
    return response.json();
  },

  addUserToOrg: async (userId: string, orgId: string) => {
    const response = await apiRequest(`/admin/users/${userId}/organizations/${orgId}`, {
      method: 'POST',
    });
    return response.json();
  },

  removeUserFromOrg: async (userId: string, orgId: string) => {
    const response = await apiRequest(`/admin/users/${userId}/organizations/${orgId}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  // Roles
  getAvailableRoles: async () => {
    const response = await apiRequest(`/admin/roles`, {
      method: 'GET',
    });
    return response.json();
  },

  assignRole: async (userId: string, roleId: string) => {
    const response = await apiRequest(`/admin/users/${userId}/roles/${roleId}`, {
      method: 'POST',
    });
    return response.json();
  },

  removeRole: async (userId: string, roleId: string) => {
    const response = await apiRequest(`/admin/users/${userId}/roles/${roleId}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  viewAsPatients: async (targetUserId: string) => {
    const response = await apiRequest(`/admin/view-as/patients`, {
      method: 'POST',
      body: JSON.stringify({ target_user_id: targetUserId }),
    });
    return response.json();
  },

  // PHA-387: Cobalt eligibility-screening rows for the admin Eligibility tab.
  getEligibility: async (params: {
    organization_id: string;
    start_date?: string;
    end_date?: string;
    search?: string;
    success_only?: boolean;
    page?: number;
    page_size?: number;
  }) => {
    const searchParams = new URLSearchParams();
    searchParams.set('organization_id', params.organization_id);
    if (params.start_date) searchParams.set('start_date', params.start_date);
    if (params.end_date) searchParams.set('end_date', params.end_date);
    if (params.search) searchParams.set('search', params.search);
    searchParams.set('success_only', params.success_only ? 'true' : 'false');
    if (params.page) searchParams.set('page', String(params.page));
    if (params.page_size) searchParams.set('page_size', String(params.page_size));
    const response = await apiRequest(`/admin/eligibility?${searchParams.toString()}`, {
      method: 'GET',
    });
    return response.json();
  },

  // PHA-566: Adoption tracking KPI reports (internal). Response includes exports
  // (csv, xlsx_base64) built from the same data as the table.
  getAdoptionTracking: async (params: {
    report: 'dashboard' | 'kpi1' | 'kpi2' | 'kpi3';
    period_type: 'month' | 'week';
    period_start: string;
    organization_id?: string;
    period_mode?: 'full_month' | 'mid_month';
    include_all_tabs?: boolean;
    history?: boolean;
    from_period?: string;
  }) => {
    const searchParams = new URLSearchParams();
    searchParams.set('report', params.report);
    searchParams.set('period_type', params.period_type);
    searchParams.set('period_start', params.period_start);
    searchParams.set('organization_id', params.organization_id || 'ALL');
    if (params.period_mode) searchParams.set('period_mode', params.period_mode);
    if (params.include_all_tabs) searchParams.set('include_all_tabs', 'true');
    if (params.history) searchParams.set('history', 'true');
    if (params.from_period) searchParams.set('from_period', params.from_period);
    const response = await apiRequest(`/admin/adoption_tracking?${searchParams.toString()}`, {
      method: 'GET',
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      throw new Error(detail?.error || `Adoption report failed (${response.status})`);
    }
    return response.json();
  },

  // PHA-1223 admin tab: per clinic, care plans that are not safely in the EHR.
  listEhrUploadIssues: async (params: {
    organization_id?: string;
    days?: number;
    include_resolved?: boolean;
    include_not_uploaded?: boolean;
  }): Promise<EhrUploadIssuesResponse> => {
    const searchParams = new URLSearchParams();
    if (params.organization_id) searchParams.set('organization_id', params.organization_id);
    if (params.days) searchParams.set('days', String(params.days));
    if (params.include_resolved) searchParams.set('include_resolved', 'true');
    if (params.include_not_uploaded) searchParams.set('include_not_uploaded', 'true');
    const response = await apiRequest(`/admin/ehr_push/issues?${searchParams.toString()}`, {
      method: 'GET',
    });
    return response.json();
  },

  // PHA-510 admin tab: paginated history of EHR sync runs.
  listEhrSyncRuns: async (params: {
    organization_id: string;
    statuses?: string[];
    started_after?: string;
    started_before?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }) => {
    const searchParams = new URLSearchParams();
    searchParams.set('organization_id', params.organization_id);
    if (params.statuses && params.statuses.length > 0) {
      searchParams.set('statuses', params.statuses.join(','));
    }
    if (params.started_after) searchParams.set('started_after', params.started_after);
    if (params.started_before) searchParams.set('started_before', params.started_before);
    if (params.search) searchParams.set('search', params.search);
    if (params.page) searchParams.set('page', String(params.page));
    if (params.page_size) searchParams.set('page_size', String(params.page_size));
    const response = await apiRequest(`/admin/ehr_sync/runs?${searchParams.toString()}`, {
      method: 'GET',
    });
    return response.json();
  },

  // PHA-510: ad-hoc per-patient EHR refresh. Spawns a daemon thread on the
  // backend; returns immediately. Backend may return 409 if already running.
  triggerEhrSync: async (params: { organization_id: string; patient_id: string }) => {
    const response = await apiRequest('/admin/ehr_sync/trigger', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      const err: Error & { status?: number; detail?: any } = new Error(
        detail?.error || `EHR sync trigger failed (${response.status})`,
      );
      err.status = response.status;
      err.detail = detail;
      throw err;
    }
    return response.json();
  },

  // PHA-510: ad-hoc per-org EHR refresh, capped by EHR_REFRESH_MAX_PATIENTS.
  triggerEhrSyncForOrg: async (params: { organization_id: string; dry_run?: boolean }) => {
    const response = await apiRequest('/admin/ehr_sync/trigger_org', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      const err: Error & { status?: number; detail?: any } = new Error(
        detail?.error || `EHR sync org trigger failed (${response.status})`,
      );
      err.status = response.status;
      err.detail = detail;
      throw err;
    }
    return response.json();
  },

  // PHA-579: ad-hoc admin-driven SmartHub AI run_nightly for one org.
  // Synchronous on the backend (capped by SMARTHUB_AI_MAX_PATIENTS).
  triggerSmartHubAiOrg: async (params: { organization_id: string }) => {
    const response = await apiRequest('/admin/smart_hub_ai/trigger_org', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      const detail = await response.json().catch(() => ({}));
      const err: Error & { status?: number; detail?: any } = new Error(
        detail?.error || `SmartHub AI trigger failed (${response.status})`,
      );
      err.status = response.status;
      err.detail = detail;
      throw err;
    }
    return response.json() as Promise<{
      status: string;
      pipeline_run_id?: string;
      organization_id?: string;
      patients_processed?: number;
      patients_attempted?: number;
      failures?: number;
    }>;
  },
};

export const scheduleApi = {
  scanSchedule: async (fileBase64: string, mimeType: string) => {
    const response = await apiRequest('/api/schedule/scan', {
      method: 'POST',
      body: JSON.stringify({ file_base64: fileBase64, mime_type: mimeType }),
    });
    return response.json();
  },
};

// Calendar Import API (PHA-212)
export const calendarApi = {
  importCalendar: async (data: {
    content: string;
    mime_type: string;
    calendar_date: string;
    is_base64?: boolean;
  }) => {
    const response = await apiRequest('/session/calendar/import', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  getLatestMatches: async () => {
    const response = await apiRequest('/session/calendar/latest-matches');
    return response.json();
  },

  listImports: async () => {
    const response = await apiRequest('/session/calendar/imports');
    return response.json();
  },

  deleteImport: async (importId: number) => {
    const response = await apiRequest(`/session/calendar/import/${importId}`, {
      method: 'DELETE',
    });
    return response.json();
  },
};

// PHA-542: Device connection history entry shape (mirrors handler.js getPatientDeviceHistory return)
export interface DeviceConnectionEntry {
  device_id: string;        // IMEI
  patient_id: string;
  added_at: string;
  added_by: string | null;
  remove_at: string | null;
  removed_by: string | null;
  serial_number: string;
  device_type: string;
  manufacturer: string;
  device_active?: boolean;
}

// PHA-1103: SmartMeter RPM integration (internal admin only).
//
// SmartMeter readings land in the SAME telli_readings table as Tellihealth ones,
// so there is no separate reading/device read API here — the existing device and
// patient-readings endpoints already serve them. What is SmartMeter-specific is
// operational: is the vendor actually sending us anything, is the webhook URL
// registered, and pulling back a window that was missed.
export interface SmartMeterStatus {
  configured: boolean;
  webhookSecretConfigured: boolean;
  manufacturer: string;
  readingsByType: Array<{ reading_type: string; count: number; last_reading: string; last_received: string }>;
  devices: { registered: number; active: number };
  orphanDevices: Array<{ device_id: string; count: number; last_reading: string }>;
  skippedSinceStart: Record<string, number>;
}

export const smartmeterApi = {
  getStatus: async (): Promise<SmartMeterStatus> => {
    const response = await apiRequest('/session/smartmeter/status');
    return response.json();
  },
  getNotifications: async () => {
    const response = await apiRequest('/session/smartmeter/notifications');
    return response.json();
  },
  registerNotification: async (notifyUrl: string, type: 'reading' | 'fulfillment' = 'reading') => {
    const response = await apiRequest('/session/smartmeter/notifications', {
      method: 'POST',
      body: JSON.stringify({ notifyUrl, type }),
    });
    return response.json();
  },
  // Pull readings back from the vendor. `hours` for a recent catch-up; start/end
  // for a historical backfill (chunked server-side to the vendor's 31-day limit).
  reconcile: async (body: { hours?: number; start?: string; end?: string; byRecordedDate?: boolean; deviceIds?: string[] }) => {
    const response = await apiRequest('/session/smartmeter/reconcile', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json();
  },
  listVendorDevices: async () => {
    const response = await apiRequest('/session/smartmeter/devices');
    return response.json();
  },
  // PHA-1124: pull each SmartMeter device's "First Reading" date and store it as
  // the device's First Data Date — the anchor for its 30-day RPM billing cycle.
  // Runs on the reconciliation sweep too; this is the "do it now" button.
  syncFirstDataDates: async (body: { deviceIds?: string[]; force?: boolean } = {}) => {
    const response = await apiRequest('/session/smartmeter/first-data-dates/sync', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response.json();
  },
};

// Tellihealth Device API
export const tellihealthApi = {
  listDevices: async (organizationId?: string) => {
    const params = organizationId ? `?organizationId=${organizationId}` : '';
    const response = await apiRequest(`/session/tellihealth/devices${params}`);
    return response.json();
  },
  // PHA-708: device_ids receiving readings but linked to no patient.
  getOrphanReadings: async (lookbackDays?: number, organizationId?: string) => {
    const qs = new URLSearchParams();
    if (lookbackDays != null) qs.set('lookbackDays', String(lookbackDays));
    if (organizationId) qs.set('organizationId', organizationId);
    const params = qs.toString() ? `?${qs.toString()}` : '';
    const response = await apiRequest(`/session/tellihealth/orphan-readings${params}`);
    return response.json();
  },
  registerDevice: async (deviceId: string, organizationId?: string, serialNumber?: string, deviceType?: string, manufacturer?: string) => {
    const response = await apiRequest('/session/tellihealth/devices', {
      method: 'POST',
      body: JSON.stringify({ deviceId, organizationId, serialNumber, deviceType, manufacturer }),
    });
    return response.json();
  },
  updateDeviceStatus: async (deviceId: string, active: boolean) => {
    const response = await apiRequest(`/session/tellihealth/devices/${deviceId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    });
    return response.json();
  },
  updateDeviceOrganization: async (deviceId: string, organizationId: string) => {
    const response = await apiRequest(`/session/tellihealth/devices/${deviceId}/organization`, {
      method: 'PATCH',
      body: JSON.stringify({ organizationId }),
    });
    return response.json();
  },
  getDeviceGen: async (deviceId: string) => {
    const response = await apiRequest(`/session/tellihealth/devices/${deviceId}/gen`);
    return response.json();
  },
  // Patient device linking
  getPatientDevice: async (patientId: string) => {
    const response = await apiRequest(`/session/patient/${patientId}/device`);
    return response.json();
  },
  // PHA-542: Full device connection history for a patient
  getPatientDeviceHistory: async (
    patientId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<{ history: DeviceConnectionEntry[]; total: number; limit: number; offset: number }> => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    const response = await apiRequest(
      `/session/patient/${patientId}/device-history${qs ? `?${qs}` : ''}`
    );
    return response.json();
  },
  getAvailableDevices: async (patientId: string, organizationId: string) => {
    const response = await apiRequest(`/session/patient/${patientId}/available-devices?organizationId=${organizationId}`);
    return response.json();
  },
  // PHA-1124: `associateOrphanReadings` backdates the link so readings that
  // arrived on this device before anyone linked it attribute to this patient.
  // Never reaches back past the device's previous patient (see the BFF handler).
  linkDevice: async (patientId: string, deviceId: string, associateOrphanReadings = false) => {
    const response = await apiRequest(`/session/patient/${patientId}/device/link`, {
      method: 'POST',
      body: JSON.stringify({ deviceId, associateOrphanReadings }),
    });
    return response.json();
  },
  // PHA-1124: how many unowned readings the checkbox above would claim, so the
  // number is visible before the link is made rather than after.
  getClaimableOrphanReadings: async (
    deviceId: string,
  ): Promise<{ count: number; firstTime: string | null; lastTime: string | null }> => {
    const response = await apiRequest(`/session/tellihealth/devices/${deviceId}/claimable-orphans`);
    return response.json();
  },
  // PHA-1124 req 2.2: admin override for the device's 30-day RPM cycle anchor.
  // `firstDataDate` is yyyy-MM-dd, or null to clear and fall back to the
  // earliest reading. Setting it exempts the device from the SmartMeter sweep.
  setDeviceFirstDataDate: async (deviceId: string, firstDataDate: string | null) => {
    const response = await apiRequest(`/session/tellihealth/devices/${deviceId}/first-data-date`, {
      method: 'PATCH',
      body: JSON.stringify({ firstDataDate }),
    });
    return response.json();
  },
  unlinkDevice: async (patientId: string, deviceId: string) => {
    const response = await apiRequest(`/session/patient/${patientId}/device/unlink`, {
      method: 'POST',
      body: JSON.stringify({ deviceId }),
    });
    return response.json();
  },
  // Billing
  getMeasurementReport: async (year?: number, month?: number) => {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (month) params.set('month', String(month));
    const qs = params.toString();
    const response = await apiRequest(`/session/tellihealth/billing/measurements${qs ? `?${qs}` : ''}`);
    return response.json();
  },
  getMeasurementTotals: async (year?: number, month?: number) => {
    const params = new URLSearchParams();
    if (year) params.set('year', String(year));
    if (month) params.set('month', String(month));
    const qs = params.toString();
    const response = await apiRequest(`/session/tellihealth/billing/totals${qs ? `?${qs}` : ''}`);
    return response.json();
  },
  getPatientReadings: async (patientId: string, params?: { fromDate?: string; toDate?: string; readingType?: string; limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.fromDate) searchParams.set('fromDate', params.fromDate);
    if (params?.toDate) searchParams.set('toDate', params.toDate);
    if (params?.readingType) searchParams.set('readingType', params.readingType);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    const response = await apiRequest(`/session/patient/${patientId}/device-readings${qs ? `?${qs}` : ''}`);
    return response.json();
  },
  // PHA-871: persist a client-generated RPM readings PDF + snapshot under a token and SMS
  // the patient a link to the public report page. No PII is stored beyond patient_id (audit only).
  sendRpmReport: async (
    patientId: string,
    params: {
      phoneNumber: string;
      startDate: string;
      endDate: string;
      pdfBase64: string;
      readings: Array<{ time: string; reading_type: string; data: Record<string, any> }>;
    },
  ) => {
    const response = await apiRequest(`/session/patient/${patientId}/rpm-report/send`, {
      method: 'POST',
      body: JSON.stringify({
        phone_number: params.phoneNumber,
        start_date: params.startDate,
        end_date: params.endDate,
        pdf_base64: params.pdfBase64,
        readings: params.readings,
      }),
    });
    return response.json();
  },
  getDeviceReadings: async (deviceId: string, params?: { fromDate?: string; toDate?: string; readingType?: string; limit?: number; offset?: number }) => {
    const searchParams = new URLSearchParams();
    if (params?.fromDate) searchParams.set('fromDate', params.fromDate);
    if (params?.toDate) searchParams.set('toDate', params.toDate);
    if (params?.readingType) searchParams.set('readingType', params.readingType);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    const response = await apiRequest(`/session/device/${encodeURIComponent(deviceId)}/readings${qs ? `?${qs}` : ''}`);
    return response.json();
  },
  removeDevice: async (deviceId: string) => {
    const response = await apiRequest(`/session/tellihealth/devices/${encodeURIComponent(deviceId)}`, {
      method: 'DELETE',
    });
    return response.json();
  },
  // Device QR scanning (PHA-374)
  createScanToken: async (organizationId?: string) => {
    const response = await apiRequest('/session/tellihealth/scan-tokens', {
      method: 'POST',
      body: JSON.stringify({ organizationId }),
    });
    return response.json();
  },
  getScanTokenResult: async (token: string) => {
    const response = await apiRequest(`/session/tellihealth/scan-tokens/${encodeURIComponent(token)}/result`);
    return response.json();
  },
};

// Clinic-scoped Tellihealth device API (for non-admin clinic users)
export const clinicTellihealthApi = {
  listDevices: async (organizationId?: string) => {
    const params = organizationId ? `?organizationId=${organizationId}` : '';
    const response = await apiRequest(`/session/clinic/tellihealth/devices${params}`);
    return response.json();
  },
  getOrganizations: async () => {
    const response = await apiRequest('/session/clinic/organizations');
    return response.json();
  },
  registerDevice: async (deviceId: string, organizationId?: string, serialNumber?: string, deviceType?: string, manufacturer?: string) => {
    const response = await apiRequest('/session/clinic/tellihealth/devices', {
      method: 'POST',
      body: JSON.stringify({ deviceId, organizationId, serialNumber, deviceType, manufacturer }),
    });
    return response.json();
  },
  updateDeviceStatus: async (deviceId: string, active: boolean) => {
    const response = await apiRequest(`/session/clinic/tellihealth/devices/${deviceId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ active }),
    });
    return response.json();
  },
  updateDeviceOrganization: async (deviceId: string, organizationId: string) => {
    const response = await apiRequest(`/session/clinic/tellihealth/devices/${deviceId}/organization`, {
      method: 'PATCH',
      body: JSON.stringify({ organizationId }),
    });
    return response.json();
  },
  removeDevice: async (deviceId: string) => {
    const response = await apiRequest(`/session/clinic/tellihealth/devices/${encodeURIComponent(deviceId)}`, {
      method: 'DELETE',
    });
    return response.json();
  },
  getDeviceGen: async (deviceId: string) => {
    const response = await apiRequest(`/session/clinic/tellihealth/devices/${deviceId}/gen`);
    return response.json();
  },
  createScanToken: async (organizationId?: string) => {
    const response = await apiRequest('/session/clinic/tellihealth/scan-tokens', {
      method: 'POST',
      body: JSON.stringify({ organizationId }),
    });
    return response.json();
  },
  getScanTokenResult: async (token: string) => {
    const response = await apiRequest(`/session/clinic/tellihealth/scan-tokens/${encodeURIComponent(token)}/result`);
    return response.json();
  },
};

// Chief of Staff AI agent
export type AgentName =
  | 'chief-of-staff'
  | 'onboarding'
  | 'outreach'
  | 'triage'
  | 'care-plan'
  | 'billing'
  | 'device'
  | 'clinic-admin';
export type AgentToolUse = {
  label: string;
  expanded: boolean;
  resolved?: 'approved' | 'denied';
  originAgent?: AgentName;
};
// PHA-549: 'system' is used for inline scope-change notices ("Now viewing
// Patient X") that the chat inserts when the user switches tabs mid-thread.
// Forwarding them to the LLM as conversation context preserves continuity
// — without them, follow-up questions like "and her last visit?" lose the
// referent when the user has just switched patients.
export type AgentChatHistoryItem = {
  role: 'user' | 'assistant' | 'system';
  content: string;
  // PHA-1169: the assistant turn's work log, echoed back so the agent can
  // reuse the exact query behind an earlier answer.
  work?: import('../components/chat/types').AgentWorkEntry[];
};

// PHA-549: context-aware scope sent with each /api/agent/chat turn so the
// agent's system prompt mirrors what SmartHub focuses on. Server-side validates
// orgId against the session and rejects mismatches with 403.
export type AgentChatScope =
  | { kind: 'patient'; patientId: string; patientName?: string; orgId: string }
  | { kind: 'clinic'; orgId: string; orgName?: string };
export type AgentPendingApproval = {
  id: string;
  method: string;
  path: string;
  body?: any;
  summary: string;
};
export type AgentTraceEvent = {
  at: string;
  agent?: AgentName;
  kind: string;
  [key: string]: any;
};
export type AgentChatResult = {
  reply: string;
  // PHA-1155: text and table parts, in reply order. See components/chat/types.
  parts?: import('../components/chat/types').MessagePart[];
  // PHA-1155: set when the agent stopped to ask a clarifying question.
  question?: import('../components/chat/types').PendingQuestion;
  // PHA-1169: see ChatMessage.workLog.
  workLog?: import('../components/chat/types').AgentWorkEntry[];
  toolUses: AgentToolUse[];
  pendingApproval?: AgentPendingApproval;
  stopReason?: string;
  agent?: AgentName;
  promptVersion?: string;
  model?: string;
  traceEvents?: AgentTraceEvent[];
};

// PHA-1155: one fragment of the answer's text, as the model writes it.
export type AgentTextDelta = { streamId: string; agent?: AgentName; text: string };

// PHA-1155: callbacks for one agent run's NDJSON stream.
export type AgentStreamHandlers = {
  onEvent?: (event: AgentTraceEvent) => void;
  onDelta?: (delta: AgentTextDelta) => void;
  // The server-side run id, sent first. Keep it: it is how a reloaded page
  // re-attaches to a run that is still going (agentApi.attachRun).
  onRun?: (runId: string) => void;
};

// Reads an agent NDJSON stream: one JSON envelope per line,
// `{ type: 'run' | 'event' | 'delta' | 'heartbeat' | 'notice' | 'final' | 'error', ... }`.
// Heartbeats only keep the connection alive through proxies and are ignored.
async function readAgentStream(
  response: Response,
  handlers: AgentStreamHandlers
): Promise<AgentChatResult> {
  if (!response.ok || !response.body) {
    let details: any = { error: 'agent_failed' };
    try { details = await response.json(); } catch { /* non-JSON */ }
    const err: any = new Error(details.details || details.error || `HTTP ${response.status}`);
    err.status = response.status;
    err.errorData = details;
    throw err;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult: AgentChatResult | null = null;

  const handleLine = (line: string) => {
    if (!line.trim()) return;
    let msg: any;
    try { msg = JSON.parse(line); } catch { return; /* skip malformed */ }
    if (msg.type === 'run' && msg.runId) {
      handlers.onRun?.(String(msg.runId));
    } else if (msg.type === 'event' && msg.event) {
      handlers.onEvent?.(msg.event as AgentTraceEvent);
    } else if (msg.type === 'delta' && typeof msg.text === 'string') {
      handlers.onDelta?.({ streamId: String(msg.streamId || ''), agent: msg.agent, text: msg.text });
    } else if (msg.type === 'final') {
      const { type, seq, ...rest } = msg;
      finalResult = rest as AgentChatResult;
    } else if (msg.type === 'error') {
      const e: any = new Error(msg.details || msg.error || 'agent_failed');
      e.status = msg.status || 500;
      e.errorData = msg;
      throw e;
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    for (const line of lines) handleLine(line);
  }
  // Flush any trailing partial.
  if (buffer.trim()) handleLine(buffer);

  if (!finalResult) {
    const e: any = new Error('Agent stream ended without a final payload');
    e.status = 502;
    throw e;
  }
  return finalResult;
}

async function streamAgent(
  payload: unknown,
  handlers: AgentStreamHandlers,
  signal?: AbortSignal
): Promise<AgentChatResult> {
  const response = await fetch(getApiUrl('/api/agent/chat'), {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    redirect: 'manual',
    headers: {
      'Content-Type': 'application/json',
      // The server switches to NDJSON when it sees this Accept header.
      Accept: 'application/x-ndjson',
    },
    body: JSON.stringify(payload),
    signal,
  });
  return readAgentStream(response, handlers);
}

// PHA-1155: the clinician's own thread history (workspace History list).
async function agentGet<T>(path: string): Promise<T> {
  const response = await fetch(getApiUrl(path), {
    method: 'GET',
    mode: 'cors',
    credentials: 'include',
    redirect: 'manual',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    const err: any = new Error(`HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export const agentApi = {
  listConversations: (limit = 30) =>
    agentGet<{ conversations: import('../components/chat/types').ConversationSummary[] }>(
      `/api/agent/conversations?limit=${encodeURIComponent(String(limit))}`
    ),
  getConversation: (conversationKey: string) =>
    agentGet<{
      conversationKey: string;
      messages: import('../components/chat/threadPersistence').StoredTurn[];
    }>(`/api/agent/conversations/${encodeURIComponent(conversationKey)}`),
  chat: async (
    message: string,
    history: AgentChatHistoryItem[] = [],
    onEvent?: (event: AgentTraceEvent) => void,
    signal?: AbortSignal,
    scope?: AgentChatScope,
    // PHA-1071: stable id for this chat thread. The BFF uses it to upsert the
    // conversation server-side, so turns land in one durable thread instead of
    // being lost with the tab.
    conversationKey?: string,
    onDelta?: (delta: AgentTextDelta) => void,
    onRun?: (runId: string) => void
  ): Promise<AgentChatResult> => {
    const payload: Record<string, unknown> = { message, history };
    if (scope) payload.scope = scope;
    if (conversationKey) payload.conversationKey = conversationKey;
    return streamAgent(payload, { onEvent, onDelta, onRun }, signal);
  },
  resume: async (
    approvalId: string,
    approved: boolean,
    reason?: string,
    onEvent?: (event: AgentTraceEvent) => void,
    signal?: AbortSignal,
    conversationKey?: string,
    // PHA-1071: everything before the assistant turn being resolved, INCLUDING
    // the user turn that opened it. That makes the resumed assistant message
    // land on the same turn_index the original request wrote, so the store
    // updates that row rather than appending a stray one.
    history?: AgentChatHistoryItem[],
    onDelta?: (delta: AgentTextDelta) => void,
    onRun?: (runId: string) => void
  ): Promise<AgentChatResult> => {
    const payload: Record<string, unknown> = {
      resumeApproval: { id: approvalId, approved, reason },
    };
    if (conversationKey) payload.conversationKey = conversationKey;
    if (history) payload.history = history;
    return streamAgent(payload, { onEvent, onDelta, onRun }, signal);
  },
  // PHA-1155: re-attach to a run after a reload or navigation. The server
  // replays the whole run, so callers reset what they had rendered first.
  // 404 = the run is gone (expired, or the server restarted).
  attachRun: async (
    runId: string,
    handlers: AgentStreamHandlers,
    signal?: AbortSignal
  ): Promise<AgentChatResult> => {
    const response = await fetch(getApiUrl(`/api/agent/runs/${encodeURIComponent(runId)}/stream`), {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      redirect: 'manual',
      headers: { Accept: 'application/x-ndjson' },
      signal,
    });
    return readAgentStream(response, handlers);
  },
  // PHA-1155: Stop button. Takes effect between model turns.
  cancelRun: async (runId: string): Promise<boolean> => {
    const response = await fetch(getApiUrl(`/api/agent/runs/${encodeURIComponent(runId)}/cancel`), {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      redirect: 'manual',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: '{}',
    });
    if (!response.ok) return false;
    const data = await response.json().catch(() => ({}));
    return !!data.cancelled;
  },
};

// ---------------------------------------------------------------------------
// PHA-1168: Custom Views. Every call carries the TAB's organization_id (from
// OrganizationContext) -- without it the BFF falls back to the login-wide session
// org, which is the cross-tab leak PHA-1123 fixed. apiRequest throws on non-2xx;
// callers read the status/code from err.errorData (see customViewErrorCode).
// ---------------------------------------------------------------------------

export type CustomViewSurface = 'patient_list' | 'billing';
// PHA-1168 Reports: a report is a saved view with kind 'report'. Its `surface` is
// the report's dataset. Callers that send no kind get 'view', as before.
export type CustomViewKind = 'view' | 'report';
export type ReportScope = 'clinic' | 'network' | 'phanova';
export type CustomViewFieldType = 'text' | 'int' | 'number' | 'date' | 'datetime' | 'bool' | 'text_list';

export interface CustomViewField {
  key: string;
  label: string;
  group: string;
  type: CustomViewFieldType;
  description: string;
  filterable: boolean;
  sortable: boolean;
  identity: boolean;
  program: string | null;
  locked: boolean;
  enum: string[];
}

export interface CustomViewManifestColumn {
  key: string;
  label: string;
  type: CustomViewFieldType;
  calculated: boolean;
  sortable: boolean;
}

export interface CustomViewStyle {
  id: number;
  target: 'row' | 'cell';
  field: string | null;
  style: 'highlight' | 'badge';
  color: 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'gray';
  text: string | null;
}

export interface CustomViewManifest {
  surface: CustomViewSurface;
  columns: CustomViewManifestColumn[];
  styles: CustomViewStyle[];
  billing?: {
    title?: string | null;
    description?: string | null;
    group_by?: string | null;
    subtotals?: string[];
    include_non_billable?: boolean;
  };
  // Reports only: aggregate measures computed over the whole result.
  summary?: ReportSummaryTable;
}

export interface ReportSummaryTable {
  group_by: string | null;
  group_label: string | null;
  measures: { key: string; label: string; fn: 'count' | 'sum' | 'avg' | 'min' | 'max'; field: string | null }[];
  rows: { group: string | null; values: Record<string, number | null> }[];
  total: Record<string, number | null>;
}

export type CustomViewRow = Record<string, unknown> & { patient_id: string; _styles: number[]; _group?: string | null };

export interface CustomViewResult {
  view?: { id: number; name: string; surface: CustomViewSurface; kind?: CustomViewKind; handle?: string };
  stale?: boolean;
  fallback?: 'standard';
  manifest: CustomViewManifest;
  records: CustomViewRow[];
  total_count: number;
  page?: number;
  page_size?: number | null;
  has_more?: boolean;
  // Billing views only
  billable_total?: number;
  hidden_billable_rows?: number;
  groups?: { key: string | null; count: number; subtotals: Record<string, number> }[];
  totals?: Record<string, number>;
  csv?: string;
  notice?: string;
  // Reports
  truncated?: boolean;
  period?: { from_date: string; to_date: string };
  summary?: ReportSummaryTable;
  /** False when the summary was computed over only the rows read (a capped Patients report). */
  summary_complete?: boolean;
}

/** What a saved report run returns (render / reports/run): csv is always present. */
export type ReportResult = CustomViewResult & { csv: string; truncated: boolean };

export interface CustomViewSummary {
  id: number;
  name: string;
  description: string | null;
  surface: CustomViewSurface;
  organization_id: string;
  organization_name: string | null;
  inherited: boolean;
  is_default: boolean;
  effective_default: boolean;
  flagged_stale: boolean;
  hides_billable_rows: boolean;
  updated_ts: string | null;
  // Reports only
  kind?: CustomViewKind;
  handle?: string;
  scope?: ReportScope;
  /** PHA-1243: the page the report is listed on. */
  home?: ReportHome;
}

/** PHA-1243: where a report lives: the Reports page, or the Billing page (billing reports made there). */
export type ReportHome = 'reports' | 'billing';

/** A saved report as listed: surface is its dataset, handle is what follows '#' in Ask Phanova. */
export type ReportSummary = CustomViewSummary & { kind: 'report'; handle: string; scope: ReportScope };
// The view definition the AI writes (backend: custom_views/spec.py).
export type CustomViewSpec = {
  surface: CustomViewSurface;
  columns: { field: string; label?: string }[];
  filters?: { field: string; op: string; value?: unknown; include_empty?: boolean }[];
  sort?: { field: string; dir: 'asc' | 'desc' }[];
  styles?: unknown[];
  calcs?: { key: string; label: string; expr: unknown }[];
  billing?: Record<string, unknown>;
  // Reports only: the aggregate table the report shows above its rows.
  summary?: { group_by?: string; measures: { fn: 'count' | 'sum' | 'avg' | 'min' | 'max'; field?: string; label?: string }[] };
  version?: number;
};

export interface CustomViewGridArgs {
  page?: number;
  page_size?: number;
  search?: string;
  filters?: Record<string, unknown>;
  column_sort?: { field: string; direction: 'asc' | 'desc' } | null;
}

export interface CustomViewBillingArgs {
  from_date?: string;
  to_date?: string;
}

/** The backend's error code (e.g. 'confirm_hidden_rows', 'view_not_found') from a thrown apiRequest error. */
export const customViewErrorCode = (err: unknown): string | undefined => {
  const data = (err as { errorData?: { error?: unknown; code?: unknown } })?.errorData;
  if (!data) return undefined;
  if (typeof data.error === 'string') return data.error;
  if (typeof data.code === 'string') return data.code;
  return undefined;
};

/** Human-readable details for a thrown custom-views error. */
export const customViewErrorDetails = (err: unknown): string[] => {
  // apiRequest keeps `error` and `details`; when the body had no `details`,
  // `details` is the raw JSON text, so the backend's `message` is parsed back out.
  const details = (err as { errorData?: { details?: unknown } })?.errorData?.details;
  if (Array.isArray(details)) return details.map(String);
  if (typeof details === 'string' && details) {
    try {
      const parsed = JSON.parse(details);
      if (Array.isArray(parsed?.details)) return parsed.details.map(String);
      if (typeof parsed?.message === 'string') return [parsed.message];
    } catch {
      return [details];
    }
  }
  if (err instanceof Error) return [err.message];
  return ['Something went wrong.'];
};

const postCustomViews = async <T,>(path: string, organizationId: string, body: Record<string, unknown> = {}): Promise<T> => {
  // JSON.stringify drops undefined, so a call without a kind sends none ('view').
  const response = await apiRequest(path, {
    method: 'POST',
    body: JSON.stringify({ ...body, organization_id: organizationId }),
  });
  return response.json();
};

export const customViewsApi = {
  enabled: (organizationId: string, kind?: CustomViewKind) =>
    postCustomViews<{ enabled: boolean; can_design: boolean }>('/session/custom_views/enabled', organizationId, { kind }),
  catalog: (organizationId: string, surface: CustomViewSurface, kind?: CustomViewKind) =>
    postCustomViews<{ surface: CustomViewSurface; catalog_version: number; fields: CustomViewField[] }>(
      '/session/custom_views/catalog', organizationId, { surface, kind }),
  // `surface` is optional for reports: omitted lists both datasets.
  list: (organizationId: string, surface?: CustomViewSurface, kind?: CustomViewKind) =>
    postCustomViews<{ surface: CustomViewSurface | null; views: CustomViewSummary[]; can_design: boolean; can_share: boolean }>(
      '/session/custom_views/list', organizationId, { surface, kind }),
  // PHA-1244: includePrompts (the builder) also recovers the prompts of a version saved
  // before they were kept, from the design log.
  get: (organizationId: string, viewId: number, kind?: CustomViewKind, opts: { includePrompts?: boolean } = {}) =>
    postCustomViews<{ id: number; name: string; description: string | null; surface: CustomViewSurface;
      spec: CustomViewSpec; organization_id: string; can_edit: boolean; is_default: boolean;
      kind?: CustomViewKind; handle?: string; home?: ReportHome;
      // PHA-1244: the builder prompts that produced the saved definition, oldest first, and
      // whether the first of them built it from nothing.
      prompts?: string[]; prompts_complete?: boolean }>(
      '/session/custom_views/get', organizationId,
      { view_id: viewId, kind, ...(opts.includePrompts ? { include_prompts: true } : {}) }),
  render: (organizationId: string, viewId: number, args: CustomViewGridArgs & CustomViewBillingArgs & { export?: boolean } = {}) =>
    postCustomViews<CustomViewResult>('/session/custom_views/render', organizationId, { view_id: viewId, ...args }),
  design: (organizationId: string, surface: CustomViewSurface, prompt: string, spec?: CustomViewSpec | null, viewId?: number | null,
    kind?: CustomViewKind) =>
    // switch_dataset: a new report asked for something only that dataset has.
    postCustomViews<{ ok: boolean; spec: CustomViewSpec | null; reply: string; errors: string[];
      switch_dataset?: CustomViewSurface }>(
      '/session/custom_views/design', organizationId, { surface, prompt, spec: spec ?? null, view_id: viewId ?? null, kind }),
  preview: (organizationId: string, surface: CustomViewSurface, spec: CustomViewSpec, args: CustomViewBillingArgs = {},
    kind?: CustomViewKind) =>
    postCustomViews<CustomViewResult>('/session/custom_views/preview', organizationId, { surface, spec, ...args, kind }),
  save: (organizationId: string, body: {
    surface: CustomViewSurface; name: string; description?: string; spec: CustomViewSpec;
    view_id?: number | null; make_default?: boolean; share_with_child_clinics?: boolean; confirm_hidden_rows?: boolean;
    kind?: CustomViewKind; home?: ReportHome; prompts?: string[]; prompts_complete?: boolean;
  }) => postCustomViews<{ view_id: number; organization_id: string; handle?: string }>('/session/custom_views/save', organizationId, body),
  // Ask Phanova #report: `report` is a handle (leading '#' optional), an id or a name.
  runReport: (organizationId: string, report: string, range: CustomViewBillingArgs = {}) =>
    postCustomViews<ReportResult>('/session/custom_views/reports/run', organizationId, { report, ...range }),
  setDefault: (organizationId: string, surface: CustomViewSurface, viewId: number | null) =>
    postCustomViews<{ ok: boolean }>('/session/custom_views/set_default', organizationId, { surface, view_id: viewId }),
  // kind 'report' lets Phanova staff archive a Phanova-wide report on "All organizations".
  archive: (organizationId: string, viewId: number, kind?: CustomViewKind) =>
    postCustomViews<{ ok: boolean }>('/session/custom_views/archive', organizationId, { view_id: viewId, kind }),
  // Internal admin rollout switch.
  enabledOrgs: async (): Promise<{ organization_ids: string[] }> => {
    const response = await apiRequest('/admin/custom_views/enabled_orgs', { method: 'POST', body: '{}' });
    return response.json();
  },
  setEnabled: async (organizationId: string, enabled: boolean): Promise<{ organization_id: string; enabled: boolean }> => {
    const response = await apiRequest('/admin/custom_views/set_enabled', {
      method: 'POST',
      body: JSON.stringify({ organization_id: organizationId, enabled }),
    });
    return response.json();
  },
};

export default API_CONFIG;
